import { planAbsorb } from '@src/services/transcript/translationAbsorbPlan';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { groupBy } from '@src/utility/groupBy';
import { LogManager } from '@src/utility/Logger';
import { joinPhraseValues, normalizeForCompare } from '@src/utility/strings';
import { debounce } from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import { normalizePhrase } from '../videoTranscriptionProcessor/translation/normalizePhrase';
import {
  buildOverrideSegment,
  type CollectedUserFix,
  collectUserFixes,
  joinFragments,
  type OverrideOrigin,
  unionOriginalElementIds,
  unionOriginalIndices,
} from './translationSpanEdit';
// Namespaced: three tiling transforms share their names with the store's
// lead-keyed wrapper methods below (PLAT-10270).
import * as translationTiling from './translationTiling';

import type { TranslationRepository } from '../repositories/TranslationRepository';
import type { TranscriptElement } from '../types.ts/transcript';
import type {
  CoverageVerdict,
  ParentPhrase,
  TranslationChange,
  TranslationData,
  TranslationHandler,
  TranslationMemoryReport,
  TranslationPhrase,
  TranslationSegment,
} from '../types.ts/translation';
import type {
  CompositionPhraseTranslation,
  CompositionTranslationOutcome,
  TranslatableTarget,
} from '../videoTranscriptionProcessor/translation/compositionTypes';
import type { RootStore } from './RootStore';
import type { DebouncedFunc } from 'lodash';

/** Shared sentinel — avoids per-paint allocations on the projection hot path. */
const EMPTY_PHRASE_MAP: ReadonlyMap<string, CompositionPhraseTranslation> =
  new Map();

export type TranslationLoadingStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'generating'
  | 'uploading'
  | 'error';

/**
 * PLAT-11690 — a translate request in flight, as `DubRun` is for a dub. Held by
 * the STORE rather than the generate popover, so the popover can close on
 * Generate and the page still knows what is running, in which language, and how
 * to stop it.
 */
export interface TranslationRun {
  /** Guards the chip against a run left over from the story just left. */
  storyId: string;
  targetLanguage: string;
  /** "Spanish (Mexico)" — the chip's hover and the run's toasts. */
  languageCaption: string;
  startedAt: number;
  /** Feeds the chip's estimated percent; see `estimateTranslateMs`. */
  etaMs: number;
}

export type TranslationRunMeta = Pick<
  TranslationRun,
  'languageCaption' | 'etaMs'
>;

/**
 * One word-level tile the splitter (T9 / PLAT-10269) produced for an override
 * segment's text. The store maps each into a derived `TranslationPhrase`
 * (`parentId` → the segment, `isUserEdited: true`, `active: true`).
 */
export interface DerivedFragmentTile {
  /** Source `initial_index` values this tile covers (a subset of the span). */
  originalIndices: number[];
  /** The tile's translated text. */
  translatedValue: string;
  /** The tile's source-text snapshot; defaults to the segment's when absent. */
  originalValueSnapshot?: string;
}

/**
 * Injected fragmentation function — the plugin side of the T9 on-demand
 * splitter (PLAT-10269). Given an override segment's text + source span, it
 * returns the word-level tiles. INJECTED (not a hard `TranslationRepository`
 * call) so `TranslationStore` stays a pure cache + ops layer that unit-tests
 * without a network: the workspace passes the real splitter call; tests pass a
 * deterministic fake; absence (or a throw) falls back to a single fragment.
 */
export type SegmentFragmenter = (args: {
  segmentId: string;
  translatedValue: string;
  originalIndices: number[];
  originalValueSnapshot: string;
}) => Promise<DerivedFragmentTile[]>;

/**
 * The outcome of `directedRetranslateWhole` (PLAT-10258). Fresh mode returns
 * both arrays empty; keep mode partitions the snapshotted fixes into the ones
 * re-attached onto the rebased translation and the ones that could not
 * re-attach (surfaced in the caller's post-run summary, never dropped).
 */
export interface DirectedRetranslateResult {
  reapplied: CollectedUserFix[];
  unresolved: CollectedUserFix[];
}

/** Shared sentinel for `getSegments` — avoids per-call empty-array allocation. */
const EMPTY_SEGMENTS: readonly TranslationSegment[] = [];

/** Segment-ordering key for a fragment: its smallest source `initial_index`
 *  (= the value the translated DOM stamps on `data-initial-index`). */
function phraseLead(p: TranslationPhrase): number {
  if (p.originalIndices.length === 0) return Number.MAX_SAFE_INTEGER;
  let lead = p.originalIndices[0];
  for (const idx of p.originalIndices) if (idx < lead) lead = idx;
  return lead;
}

const SAVE_DEBOUNCE_MS = 2000;

/**
 * Pure data cache + ops for translations. Holds per-language translation JSON,
 * per-language pending edits (debounced auto-save), and per-language loading
 * status. Deliberately does NOT hold "activeLanguage" or any view-selection
 * state — each consuming view owns its own language selection so multiple
 * views can render different languages simultaneously off the same cache.
 */
export class TranslationStore {
  /** Translation data cache, keyed by BCP 47 language code. */
  translations: Map<string, TranslationData> = new Map();

  /**
   * Per-language pending edits.
   * Outer map: language code → inner map.
   * Inner map: originalIndices-hash → translated value (unsaved).
   */
  pendingEdits: Map<string, Map<string, string>> = new Map();

  /** Per-language loading state for UI indicators. */
  loadingStatus: Map<string, TranslationLoadingStatus> = new Map();

  /**
   * PLAT-11690 — the translate request in flight, if any, as `DubbingStore`
   * holds a dub's. One at a time.
   *
   * This is NOT a second copy of `loadingStatus`. That map answers a
   * per-language CACHE question ("has this language been loaded, is it stale,
   * did it error") and is read by the store itself. This answers a PAGE
   * question: is something translating here, in which language, since when,
   * and how do I stop it — everything the on-page chip needs to name the run
   * and cancel it, none of which a status enum can carry. Owning it here is
   * what lets the generate popover close on Generate: the run outlives it.
   */
  activeRun: TranslationRun | null = null;

  /**
   * Languages this showcase supports. Starts empty; populated externally by a
   * later ticket that wires `supportedLanguages` from the showcase record.
   */
  availableLanguages: string[] = [];

  /**
   * Editorial context about the active story. Forwarded on every
   * generate/regenerate call so LLM handlers can fold it into their
   * system prompt (register, terminology, tone). Populated externally
   * on story load; cleared / reset when the active story changes.
   */
  storyContext: { title?: string; summary?: string } | null = null;

  /**
   * The story this cache currently holds data for, or `null` when nothing has
   * claimed it. Set through `setStory`, which clears the cache whenever the
   * claim moves to a different story.
   *
   * The cache is story-scoped but lives for the whole app session (it hangs off
   * `RootStore`), so it has to carry its own story identity: without it the only
   * thing standing between one story's translations and another story's dropdown
   * was a component staying mounted across the switch, which none of them do
   * (PLAT-11557).
   */
  cachedStoryId: string | null = null;

  private rootStore: RootStore;
  private logger = LogManager.getLogger('store:TranslationStore');

  /**
   * Cancel handle for `activeRun`; not observable (a DOM object, nothing reads
   * it reactively).
   */
  private _runAbort: AbortController | null = null;

  /**
   * Bumped by every `reset()`. Every async op captures it before its first
   * await (via `watchCacheGeneration`) and drops its writes if it changed, so
   * an op started for the previous story can't land in the new story's cache
   * after the switch.
   */
  private _cacheGeneration = 0;

  /**
   * PLAT-9966: memoized per-language Map keyed by `normalizePhrase(sourceText)`
   * for O(1) lookups during the per-paint composition translation projection.
   * Keyed by the `TranslationData` reference — replacing the cache entry
   * (which every write path does) invalidates the memo automatically.
   *
   * Heavier than the canonical `compositionPhrases[]` array on the data
   * object so we'd rather not store it persisted; cheap enough to rebuild
   * on demand for the active language.
   */
  private _compositionPhraseLookupMemo: WeakMap<
    TranslationData,
    Map<string, CompositionPhraseTranslation>
  > = new WeakMap();

  /**
   * PLAT-10260: memoized reconstructed segments per language, keyed by the
   * `phrases` array reference (not the whole `TranslationData`). Every write
   * path replaces `phrases` together with the data object, so this invalidates
   * automatically and stays correct even if a future op swaps `phrases` alone.
   */
  private _segmentsMemo: WeakMap<TranslationPhrase[], TranslationSegment[]> =
    new WeakMap();

  /** Per-language debounced save functions. Created lazily on first edit. */
  private _debouncedSaves: Map<string, DebouncedFunc<() => void>> = new Map();

  /** In-flight save promises per language, awaited by `flushChanges()`. */
  private _inflightSaves: Map<string, Promise<void>> = new Map();

  /**
   * Subscribers notified synchronously when a language is removed. Plain
   * callbacks (not MobX) so views can capture cursor Y / reset selection
   * in the same tick the eviction lands, before the next render.
   */
  private _languageRemovedListeners: Set<(language: string) => void> =
    new Set();

  /**
   * PLAT-11649 — ambient save state, per language, for the Translation
   * Review's "Changes save automatically / Saving… / Saved" status. A COUNT of
   * persists in flight (an edit's debounced `saveEdits` and a confirmed
   * action's `saveTranslationData` can overlap), and the time the last one
   * landed. Observable UI state only: nothing here does IO.
   */
  savingLanguages: Map<string, number> = new Map();
  lastSavedAt: Map<string, number> = new Map();

  /**
   * Save-failure listeners (same shape as `_languageRemovedListeners`). Both
   * persist paths log and — for `saveTranslationData` — swallow, so without
   * this a failed save has no observable signal a surface could act on.
   */
  private _saveFailedListeners: Set<
    (language: string, error: unknown) => void
  > = new Set();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable<
      this,
      | 'rootStore'
      | 'logger'
      | '_cacheGeneration'
      | '_debouncedSaves'
      | '_inflightSaves'
      | '_languageRemovedListeners'
      | '_saveFailedListeners'
      | '_compositionPhraseLookupMemo'
      | '_segmentsMemo'
      | '_runAbort'
    >(this, {
      translations: observable.shallow,
      activeRun: observable.ref,
      pendingEdits: observable.shallow,
      loadingStatus: observable.shallow,
      savingLanguages: observable.shallow,
      lastSavedAt: observable.shallow,
      rootStore: false,
      logger: false,
      _cacheGeneration: false,
      _debouncedSaves: false,
      _inflightSaves: false,
      _languageRemovedListeners: false,
      _saveFailedListeners: false,
      _compositionPhraseLookupMemo: false,
      _segmentsMemo: false,
      _runAbort: false,
    });
  }

  setAvailableLanguages(langs: string[]): void {
    this.availableLanguages = langs;
  }

  /**
   * Point the cache at `storyId`, clearing it first when it currently holds a
   * DIFFERENT story. No-op when it already holds this story, so the many
   * surfaces that call this (five today) stay idempotent and a clip-preview
   * modal opening over the active story doesn't discard its pending edits.
   *
   * This is the whole story-scoping contract: callers say which story they are
   * showing and the cache keeps itself honest. It deliberately does NOT depend
   * on observing a transition — the previous approach (a ref in
   * `useTranslationStoreHydration` comparing the last bridged id) could only
   * see a story change while a consumer stayed mounted across it, and Content
   * Studio unmounts every consumer behind `ContentStudioSkeleton` while the new
   * story loads. Each one then remounted with the new id already in hand, read
   * its ref as "first bridge, cache is empty", skipped the reset, and left the
   * previous story's languages in the dropdown of a story that was never
   * translated (PLAT-11557).
   */
  setStory(storyId: string | null): void {
    if (this.cachedStoryId === storyId) return;
    // An UNCLAIMED cache is adopted, not cleared: nothing populates this store
    // without a surface having claimed the story first, so `null` means either
    // an empty cache (app start) or one a sibling surface pre-warmed for this
    // same story before its claim landed. Only a claim on a DIFFERENT story
    // proves the contents are foreign.
    if (this.cachedStoryId !== null) this.reset();
    this.cachedStoryId = storyId;
  }

  /**
   * Drop all per-story state so a story switch starts from a clean cache.
   * Prefer `setStory` — it resets only when the story actually changes.
   * Also releases the story claim and invalidates any in-flight discovery.
   *
   * Cancels queued debounced saves; trailing edits that hadn't yet fired
   * are dropped (matches the trade-off the user makes on hard refresh).
   * In-flight saves are dropped from the waiting list but NOT cancelled: the
   * POST completes on its own either way, and `_inflightSaves` only decides
   * who awaits it. Clearing keeps the OUTGOING story's save from rejecting
   * the incoming story's `flushChanges` (or being awaited by its
   * `removeLanguage`); the generation guard in `saveEdits` is what stops its
   * writeback. The identity guard in that finally block makes the double
   * removal harmless.
   */
  reset(): void {
    // PLAT-11690 — before the generation bump, not after. `superseded()` would
    // drop the outgoing story's writes either way, but only the abort stops the
    // request itself: without it an LLM translate keeps burning against a story
    // nobody is looking at, and `activeRun` would point at it until it landed.
    this.cancelRun();
    for (const fn of this._debouncedSaves.values()) fn.cancel();
    this._debouncedSaves.clear();
    this._inflightSaves.clear();
    // Defensive: clear any listeners too so a non-React subscriber that
    // forgot to unsubscribe doesn't keep firing across stories. React-
    // owned subscribers (the six current views) clean themselves up via
    // useEffect; this guards future code that may not.
    this._languageRemovedListeners.clear();
    this._saveFailedListeners.clear();
    this._cacheGeneration += 1;
    runInAction(() => {
      this.translations = new Map();
      this.pendingEdits = new Map();
      this.loadingStatus = new Map();
      this.storyContext = null;
      this.cachedStoryId = null;
      this.savingLanguages = new Map();
      this.lastSavedAt = new Map();
    });
  }

  /**
   * Capture the cache's identity before an await. The returned predicate says
   * whether `reset()` has run since — i.e. whether the story this op belongs
   * to is still the one the cache holds.
   *
   * EVERY post-await write to `translations` / `loadingStatus` must be guarded
   * with it. Neither `reset` nor `setStory` can cancel an already-started op,
   * so an unguarded write finishes by putting the outgoing story's data into
   * the incoming story's cache — and because the "already cached" checks then
   * short-circuit, the new story never loads its own (PLAT-11557).
   */
  private watchCacheGeneration(): () => boolean {
    const generation = this._cacheGeneration;
    return () => this._cacheGeneration !== generation;
  }

  /**
   * Provide editorial context (title + summary) for the active story.
   * Passing `null` clears it. Callers should update this whenever the
   * active story changes so in-flight translations of the new story
   * don't carry stale context.
   */
  setStoryContext(ctx: { title?: string; summary?: string } | null): void {
    this.storyContext = ctx;
  }

  /**
   * PLAT-11898 — the languages this story has a saved translation in, for a
   * caller that needs the answer before (and regardless of) discovery:
   * Creator Studio's entry check of a remembered language. IO in a store on
   * purpose, like `discoverTranslations`; it touches no store state.
   */
  async listTranslations(storyId: string): Promise<string[]> {
    return this.repository.listTranslations(storyId);
  }

  /**
   * Populate the cache with any translations that already exist on S3 for
   * this story. Called on Transcript & Speakers mount so the language
   * toggle shows previously-generated languages without the user having to
   * regenerate. Idempotent: languages already in the cache are skipped.
   *
   * Soft-fails on individual language fetches (logs + continues) — a bad
   * blob on one language shouldn't prevent the others from loading.
   *
   * Abandons its writes if the cache is reset while it runs. Both of its awaits
   * are wide enough for a story switch to land mid-flight, and neither `reset`
   * nor `setStory` can cancel an already-started pass — so a discovery for the
   * outgoing story would otherwise finish by writing that story's blobs into the
   * incoming story's cache (PLAT-11557).
   */
  async discoverTranslations(storyId: string): Promise<void> {
    this.logger.info('discoverTranslations: starting', { storyId });
    const superseded = this.watchCacheGeneration();
    let languages: string[];
    try {
      languages = await this.repository.listTranslations(storyId);
    } catch (error) {
      this.logger.error('discoverTranslations: list failed', {
        storyId,
        ...extractErrorInfo(error),
      });
      return;
    }
    if (superseded()) {
      this.logger.info('discoverTranslations: abandoned, cache moved on', {
        storyId,
      });
      return;
    }
    // Skip languages that are already cached OR whose status indicates an
    // in-flight user action (generate/regenerate/srt-upload) or a parallel
    // discovery pass. Touching them here would race with the operation that
    // owns the status — a generate or upload could flip to 'loaded' while
    // we flip to 'loading', producing an inconsistent UI or (worse) reading
    // a half-written `latest.json` over the in-flight write's eventual
    // result.
    const toFetch = languages.filter(lang => {
      if (this.translations.has(lang)) return false;
      const status = this.loadingStatus.get(lang);
      return (
        status !== 'generating' &&
        status !== 'loading' &&
        status !== 'uploading'
      );
    });
    if (toFetch.length === 0) {
      this.logger.info('discoverTranslations: nothing to fetch', {
        storyId,
        listed: languages.length,
      });
      return;
    }

    runInAction(() => {
      for (const l of toFetch) this.loadingStatus.set(l, 'loading');
    });

    // Guard every status flip below with a stale check: only transition
    // when the status we set above is still 'loading'. If a generate()
    // call started mid-discovery, its 'generating' / 'loaded' / 'error'
    // state takes precedence and we leave it alone.
    const setIfStillLoading = (
      lang: string,
      next: TranslationLoadingStatus,
    ) => {
      if (superseded()) return;
      if (this.loadingStatus.get(lang) === 'loading') {
        this.loadingStatus.set(lang, next);
      }
    };

    await Promise.all(
      toFetch.map(async lang => {
        try {
          const data = await this.repository.fetchTranslationByLanguage(
            storyId,
            lang,
          );
          if (!data) {
            // Listed but absent — treat as not loaded; some other session
            // may have cleaned it up between list and get.
            runInAction(() => {
              setIfStillLoading(lang, 'idle');
            });
            return;
          }
          runInAction(() => {
            if (superseded()) return;
            if (this.loadingStatus.get(lang) !== 'loading') return;
            this.translations.set(lang, data);
            this.loadingStatus.set(lang, 'loaded');
          });
        } catch (error) {
          runInAction(() => {
            setIfStillLoading(lang, 'error');
          });
          this.logger.error('discoverTranslations: fetch failed', {
            storyId,
            lang,
            ...extractErrorInfo(error),
          });
        }
      }),
    );
    this.logger.info('discoverTranslations: completed', {
      storyId,
      fetched: toFetch.length,
    });
  }

  private get repository(): TranslationRepository {
    if (!this.rootStore.datoClientStore.translationRepository) {
      throw new Error('Translation repository not initialized');
    }
    return this.rootStore.datoClientStore.translationRepository;
  }

  // --- Reads --------------------------------------------------------------

  getTranslation(lang: string): TranslationData | null {
    return this.translations.get(lang) ?? null;
  }

  getLoadingStatus(lang: string): TranslationLoadingStatus {
    return this.loadingStatus.get(lang) ?? 'idle';
  }

  /**
   * Whether a pending (unsaved) user edit exists for the given phrase. Used by
   * consumers that need to distinguish a pending edit from the cached value
   * without re-hashing the originalIndices themselves.
   */
  hasPendingEdit(lang: string, originalIndices: number[]): boolean {
    const pendingForLang = this.pendingEdits.get(lang);
    if (!pendingForLang) return false;
    return pendingForLang.has(hashOriginalIndices(originalIndices));
  }

  /**
   * Returns the currently-displayed translated text for a phrase:
   *   1. pending edit, if any (unsaved user input)
   *   2. else the phrase's `translatedValue` from the cache
   *   3. else null
   *
   * @deprecated Integer-keyed. Its only production caller (the Tier-2 caption
   * derivation in `useTranslatedCaptionLines`) was removed. Retained only as a
   * store-test accessor; scheduled for deletion in the identity phase of the
   * translation-architecture refactor (see docs/refactors/translation-architecture-refactor.md),
   * when the store tests re-key to `PhraseId`. Do not add new callers — use
   * `getTranslatedPhraseByElementIds`.
   */
  getTranslatedPhrase(lang: string, originalIndices: number[]): string | null {
    const hash = hashOriginalIndices(originalIndices);
    const pendingForLang = this.pendingEdits.get(lang);
    if (pendingForLang && pendingForLang.has(hash)) {
      return pendingForLang.get(hash) ?? null;
    }
    const data = this.translations.get(lang);
    if (!data) return null;
    const phrase = data.phrases.find(
      p => hashOriginalIndices(p.originalIndices) === hash,
    );
    return phrase ? phrase.translatedValue : null;
  }

  /**
   * PLAT-10611 (F4) — the STABLE-ID identity twin of `getTranslatedPhrase`.
   * Resolves the currently-displayed translated text for a phrase keyed by its
   * `originalElementIds` (the id-only identity path used by the render hooks +
   * editor popover), NOT the positional integer. Id-ONLY: an id-less phrase
   * (`undefined` / empty) returns null — dropped, never integer-matched
   * (CALIBRATION plat-10611-10 clean-break). Callers fall back to the phrase's
   * own `translatedValue`, which the optimistic fold keeps current, so display
   * stays correct even for id-less legacy phrases. The integer
   * `getTranslatedPhrase` stays for the caption + pure-join callers.
   */
  getTranslatedPhraseByElementIds(
    lang: string,
    originalElementIds: string[] | undefined,
  ): string | null {
    if (!originalElementIds || originalElementIds.length === 0) return null;
    const hash = hashOriginalElementIds(originalElementIds);
    const pendingForLang = this.pendingEdits.get(lang);
    if (pendingForLang && pendingForLang.has(hash)) {
      return pendingForLang.get(hash) ?? null;
    }
    const data = this.translations.get(lang);
    if (!data) return null;
    const phrase = data.phrases.find(
      p =>
        p.originalElementIds !== undefined &&
        p.originalElementIds.length > 0 &&
        hashOriginalElementIds(p.originalElementIds) === hash,
    );
    return phrase ? phrase.translatedValue : null;
  }

  /**
   * PLAT-10611 (F4) — the STABLE-ID identity twin of `hasPendingEdit`. Whether
   * an unsaved edit exists for the phrase keyed by its `originalElementIds`.
   * Id-ONLY (no integer fallback): an id-less phrase returns false. The integer
   * `hasPendingEdit` stays for the caption + pure-join callers.
   */
  hasPendingEditByElementIds(
    lang: string,
    originalElementIds: string[] | undefined,
  ): boolean {
    if (!originalElementIds || originalElementIds.length === 0) return false;
    const pendingForLang = this.pendingEdits.get(lang);
    if (!pendingForLang) return false;
    return pendingForLang.has(hashOriginalElementIds(originalElementIds));
  }

  /**
   * PLAT-9966: lookup map for the composition translation projection. Returns
   * an empty Map when no translation is cached or `compositionPhrases` is
   * absent. The returned Map is shared across calls for the same underlying
   * `TranslationData` reference; replacing the cache entry produces a fresh
   * Map on the next call.
   *
   * Reads through `translations.get(lang)` — the underlying `observable.shallow`
   * Map — so callers that subscribe to this getter inside a MobX reaction
   * pick up cache-entry replacements.
   */
  getCompositionPhrasesByNormalizedSource(
    lang: string,
  ): ReadonlyMap<string, CompositionPhraseTranslation> {
    const data = this.translations.get(lang);
    if (!data) return EMPTY_PHRASE_MAP;
    const phrases = data.compositionPhrases;
    if (!Array.isArray(phrases) || phrases.length === 0) {
      return EMPTY_PHRASE_MAP;
    }
    const cached = this._compositionPhraseLookupMemo.get(data);
    if (cached) return cached;
    const built = new Map<string, CompositionPhraseTranslation>();
    for (const entry of phrases) {
      if (!entry || typeof entry.sourceText !== 'string') continue;
      built.set(normalizePhrase(entry.sourceText), entry);
    }
    this._compositionPhraseLookupMemo.set(data, built);
    return built;
  }

  /**
   * PLAT-10260: reconstruct the Segment view's units. Returns the **active**
   * fragments (`active !== false`, D10) grouped by `parentId` — one
   * `TranslationSegment` per segment, fragments in source order, segments in
   * source order. Fallback: a fragment with no `parentId` (unsplit) forms its
   * own segment (`solo:<lead>`), so Segment view is usable before any split.
   *
   * Memoized on the `phrases` array reference; recomputes only when the cache
   * entry is replaced. Reads through `translations.get(lang)` (the
   * `observable.shallow` Map), so callers inside a MobX reaction re-run on
   * cache-entry replacement — same contract as the composition lookup above.
   */
  getSegments(lang: string): readonly TranslationSegment[] {
    const data = this.translations.get(lang);
    if (!data) return EMPTY_SEGMENTS;
    const phrases = data.phrases;
    if (!phrases || phrases.length === 0) return EMPTY_SEGMENTS;

    const cached = this._segmentsMemo.get(phrases);
    if (cached) return cached;

    // Only active fragments participate (retired/override-originals excluded).
    const active = phrases.filter(p => p.active !== false);
    // Group by stable segment id. A split/override fragment keys on its
    // `parentId`; an unsplit fragment keys on its own lead ELEMENT id — the
    // same stable identity the coverage popover addresses the split controls
    // by (`openFragment.parentId ?? leadElementUuid`, PLAT-10268 stable-id
    // migration). Falls back to `solo:<lead>` only when a fragment carries no
    // element id, so it still forms its own one-fragment segment.
    const grouped = groupBy(
      active,
      p => p.parentId ?? p.originalElementIds?.[0] ?? `solo:${phraseLead(p)}`,
    );

    const parentById = new Map<string, ParentPhrase>();
    for (const seg of data.parentPhrases ?? []) parentById.set(seg.id, seg);

    const segments: TranslationSegment[] = [];
    for (const [id, fragments] of grouped) {
      const ordered = [...fragments].sort(
        (a, b) => phraseLead(a) - phraseLead(b),
      );
      segments.push({ id, fragments: ordered, parent: parentById.get(id) });
    }
    // Segments in source order (by each segment's earliest source index).
    segments.sort(
      (a, b) => phraseLead(a.fragments[0]) - phraseLead(b.fragments[0]),
    );

    this._segmentsMemo.set(phrases, segments);
    return segments;
  }

  // --- Ops ----------------------------------------------------------------

  /**
   * Fetch translation JSON. Idempotent — returns early if already loaded.
   * Caller passes the resolved S3 URL. URL resolution from the showcase
   * record ships in a later ticket.
   */
  async loadTranslation(lang: string, url?: string): Promise<void> {
    if (this.translations.has(lang)) return;
    if (!url) {
      throw new Error(
        `TranslationStore.loadTranslation: no URL resolved for ${lang} (showcase wiring ships in a later ticket)`,
      );
    }

    const superseded = this.watchCacheGeneration();
    runInAction(() => {
      this.loadingStatus.set(lang, 'loading');
    });
    try {
      const data = await this.repository.fetchTranslation(url);
      runInAction(() => {
        if (superseded()) return;
        this.translations.set(lang, captureMachineOriginals(data));
        this.loadingStatus.set(lang, 'loaded');
      });
    } catch (error) {
      runInAction(() => {
        if (superseded()) return;
        this.loadingStatus.set(lang, 'error');
      });
      this.logger.error('loadTranslation failed', {
        lang,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * PLAT-11690 — `generateTranslation` as a RUN the page owns: registered here
   * before the first await, so the generate popover can close the moment the
   * user presses Generate and the on-page chip takes over without a gap.
   *
   * It delegates rather than duplicating the request. `generateTranslation`
   * keeps sole ownership of the cache writes, the supersede guard and the
   * logging; this adds only the three things a closed popover takes away —
   * WHICH language is running, since WHEN, and a cancel that outlives the UI
   * that started it.
   *
   * `options.prepare` is the caller's pre-flight (the transcript re-densify +
   * S3 persist the Transcript & Speakers host needs) awaited INSIDE the run, on
   * purpose: run first, prepare second, so there is never a moment with neither
   * popover nor chip. Its rejection fails the run and is the caller's to
   * describe.
   *
   * Throws `AbortError` when cancelled — `generateTranslation` swallows it and
   * returns normally, which would otherwise read to a caller as a clean success
   * and announce a translation that never happened.
   */
  async startRun(
    targetLang: string,
    options: {
      handler?: TranslationHandler;
      compositionTargets?: TranslatableTarget[];
      showcaseId?: string;
      prepare?: () => Promise<void>;
    } = {},
    meta: TranslationRunMeta,
  ): Promise<{
    compositions?: CompositionTranslationOutcome;
    translationMemoryReport?: TranslationMemoryReport;
  }> {
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error('TranslationStore.startRun: no storyId on RootStore');
    }
    // One at a time, the dub idiom. Callers are expected to keep the user from
    // starting a second (the toggle's generate entries go inert while a run is
    // live), so this is the backstop, not the guard.
    this._runAbort?.abort();
    const controller = new AbortController();
    this._runAbort = controller;
    const run: TranslationRun = {
      storyId,
      targetLanguage: targetLang,
      languageCaption: meta.languageCaption,
      etaMs: meta.etaMs,
      startedAt: Date.now(),
    };
    runInAction(() => {
      this.activeRun = run;
    });
    try {
      await options.prepare?.();
      // Cancelled during the pre-flight: the request must not go out at all
      // (a fetch handed an already-aborted signal would reject anyway, but a
      // slower `prepare` would otherwise still be followed by a server call).
      if (controller.signal.aborted) {
        throw new DOMException('Translation run aborted', 'AbortError');
      }
      const result = await this.generateTranslation(targetLang, {
        handler: options.handler,
        compositionTargets: options.compositionTargets,
        showcaseId: options.showcaseId,
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        throw new DOMException('Translation run aborted', 'AbortError');
      }
      return result;
    } finally {
      runInAction(() => {
        // A newer startRun may already own activeRun — leave it alone.
        if (this.activeRun === run) {
          this.activeRun = null;
          this._runAbort = null;
        }
      });
    }
  }

  /**
   * Abort the run in flight (no-op when idle). The server may still finish the
   * job; if so the translation shows up on the next read — the same trade
   * `DubbingStore.cancelRun` makes.
   */
  cancelRun(): void {
    if (!this._runAbort) return;
    this.logger.info('Translation run cancelled', {
      storyId: this.activeRun?.storyId,
      targetLanguage: this.activeRun?.targetLanguage,
    });
    this._runAbort.abort();
    this._runAbort = null;
    runInAction(() => {
      this.activeRun = null;
    });
  }

  /**
   * Generate a new translation for `targetLang`. Sources transcript elements
   * and source language from RootStore.
   *
   * `options.signal` cancels the in-flight fetch. A cancelled generation is
   * treated as a return to `'idle'` (user action, not a failure) — no error
   * log, no re-throw. `startRun` above wraps this for the page-owned case and
   * re-asserts that abort for its callers.
   */
  async generateTranslation(
    targetLang: string,
    options: {
      handler?: TranslationHandler;
      signal?: AbortSignal;
      /**
       * PLAT-9967. Plugin-pre-extracted translatable composition targets
       * (see `extractTranslatableElements`). When non-empty, the backend
       * runs the composition pipeline alongside the transcript pass and
       * the returned outcome surfaces via `compositions` so callers can
       * render per-section status. Omit (or pass empty) for transcript-
       * only translation; the request body shape is unchanged from the
       * pre-PLAT-9967 behaviour in that case.
       */
      compositionTargets?: TranslatableTarget[];
      /**
       * The showcase the user is translating in (PLAT-10875). The store can't
       * reach the legacy VideoCreatorStore, so the React caller supplies it
       * (videoCreator.organization?.id). Forwarded so the backend honors that
       * workspace's translation memory when the story is in multiple showcases.
       */
      showcaseId?: string;
    } = {},
  ): Promise<{
    compositions?: CompositionTranslationOutcome;
    /**
     * PLAT-10924 — the run's translation-memory QA outcome, passed straight
     * through from the response for the caller's analytics. Absent when the run
     * applied no glossary entry. Not stored: it describes the run, not the
     * translation.
     */
    translationMemoryReport?: TranslationMemoryReport;
  }> {
    const { handler, signal, compositionTargets, showcaseId } = options;
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.generateTranslation: no storyId on RootStore',
      );
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;

    // PLAT-9967: forward any existing composition phrases the client
    // already has cached for this language so the backend can skip its
    // own S3 read on the merge pass. Safe when absent (backend reads
    // `latest.json` itself).
    const existingCompositionPhrases =
      this.translations.get(targetLang)?.compositionPhrases;

    const superseded = this.watchCacheGeneration();
    runInAction(() => {
      this.loadingStatus.set(targetLang, 'generating');
    });
    this.logger.info('generateTranslation: starting', {
      storyId,
      sourceLanguage,
      targetLang,
      handlerId: handler?.id,
      compositionTargetCount: compositionTargets?.length ?? 0,
    });
    try {
      // Do NOT ship `elements` in the request body. The server fetches the
      // canonical transcript from S3 via storyId + sourceLanguage; sending
      // the plugin's in-memory copy is both redundant (the transcript is
      // already on S3) and hits Next.js body-parser limits for long content.
      // Matches the established pattern for transcript-addressed endpoints.
      const { translation, compositions, translationMemoryReport } =
        await this.repository.generateTranslation({
          storyId,
          showcaseId,
          sourceLanguage,
          targetLanguage: targetLang,
          handler,
          signal,
          storyContext: this.storyContext ?? undefined,
          ...(compositionTargets && compositionTargets.length > 0
            ? { compositionTargets }
            : {}),
          ...(existingCompositionPhrases ? { existingCompositionPhrases } : {}),
        });
      runInAction(() => {
        if (superseded()) return;
        this.translations.set(targetLang, captureMachineOriginals(translation));
        this.loadingStatus.set(targetLang, 'loaded');
      });
      this.logger.info('generateTranslation: completed', {
        targetLang,
        phraseCount: translation.phrases?.length,
        compositionPhraseCount: translation.compositionPhrases?.length ?? 0,
        compositionsStatus: compositions?.status,
        tmMissed: translationMemoryReport?.missed.length,
        tmRetried: translationMemoryReport?.retried.length,
      });
      return { compositions, translationMemoryReport };
    } catch (error) {
      if (isAbortError(error)) {
        runInAction(() => {
          if (superseded()) return;
          this.loadingStatus.set(targetLang, 'idle');
        });
        this.logger.info('generateTranslation: aborted', { targetLang });
        return {};
      }
      runInAction(() => {
        if (superseded()) return;
        this.loadingStatus.set(targetLang, 'error');
      });
      this.logger.error('generateTranslation failed', {
        targetLang,
        handlerId: handler?.id,
        compositionTargetCount: compositionTargets?.length ?? 0,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Install a customer-uploaded SRT as the translation for `targetLang`.
   * Mirrors `generateTranslation` shape — same `loadingStatus` lifecycle
   * (with `'uploading'` instead of `'generating'`), same observable-map
   * update on success — but routes through the `import-srt-v1` handler
   * server-side (PLAT-9778). The SRT itself is the translation; the
   * server timestamp-aligns cues to original-transcript words and runs
   * the existing sentence splitter.
   *
   * Returns a coverage summary so the modal can decide whether to
   * surface a partial-coverage warning.
   */
  async uploadSrtTranslation(
    targetLang: string,
    file: File,
    options: { signal?: AbortSignal } = {},
  ): Promise<{ coveragePercent: number; uncoveredCount: number }> {
    const { signal } = options;
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.uploadSrtTranslation: no storyId on RootStore',
      );
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;

    const superseded = this.watchCacheGeneration();
    runInAction(() => {
      this.loadingStatus.set(targetLang, 'uploading');
    });
    this.logger.info('uploadSrtTranslation: starting', {
      storyId,
      sourceLanguage,
      targetLang,
      filename: file.name,
      sizeBytes: file.size,
    });
    try {
      const srtBody = await file.text();
      const { data, coverage } = await this.repository.uploadSrtTranslation({
        storyId,
        sourceLanguage,
        targetLanguage: targetLang,
        filename: file.name,
        srtBody,
        signal,
      });
      runInAction(() => {
        if (superseded()) return;
        this.translations.set(targetLang, captureMachineOriginals(data));
        this.loadingStatus.set(targetLang, 'loaded');
      });
      this.logger.info('uploadSrtTranslation: completed', {
        targetLang,
        phraseCount: data.phrases?.length,
        coveragePercent: coverage.coveragePercent,
        uncoveredCount: coverage.uncoveredOriginalIndices.length,
      });
      return {
        coveragePercent: coverage.coveragePercent,
        uncoveredCount: coverage.uncoveredOriginalIndices.length,
      };
    } catch (error) {
      if (isAbortError(error)) {
        runInAction(() => {
          if (superseded()) return;
          this.loadingStatus.set(targetLang, 'idle');
        });
        this.logger.info('uploadSrtTranslation: aborted', { targetLang });
        throw error;
      }
      runInAction(() => {
        if (superseded()) return;
        this.loadingStatus.set(targetLang, 'error');
      });
      this.logger.error('uploadSrtTranslation failed', {
        targetLang,
        filename: file.name,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Regenerate — destructive rebuild (full) or scoped rerun (partial). The
   * server archives the prior original/latest before rewriting; this store
   * swaps the cached TranslationData with the fresh result on success. Any
   * pending unsaved edits for this language are cleared after a full
   * regenerate since the phrase set is rebuilt from scratch.
   *
   * Uses handler defaults server-side; no per-call overrides are plumbed
   * yet — the UI's "Regenerate" affordance intentionally matches a clean
   * first-generate run.
   */
  async regenerateTranslation(
    targetLang: string,
    scope: 'full' | 'partial',
    // POSITIONS into the stored `phrases[]` array (NOT source `initial_index`) —
    // the index space the backend partial scope bounds-checks. Required for
    // partial; the caller must map source indices → positions before calling.
    phraseIndices?: number[],
    options: {
      handler?: TranslationHandler;
      signal?: AbortSignal;
      /** PLAT-10256 free-text steering forwarded to the repo (whole/partial scope). */
      instruction?: string;
    } = {},
  ): Promise<void> {
    if (scope === 'partial' && (!phraseIndices || phraseIndices.length === 0)) {
      throw new Error(
        'TranslationStore.regenerateTranslation: partial scope requires phraseIndices (positions into phrases[])',
      );
    }
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.regenerateTranslation: no storyId on RootStore',
      );
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;

    const superseded = this.watchCacheGeneration();
    runInAction(() => {
      this.loadingStatus.set(targetLang, 'generating');
    });
    this.logger.info('regenerateTranslation: starting', {
      storyId,
      sourceLanguage,
      targetLang,
      scope,
      handlerId: options.handler?.id,
    });
    try {
      const response = await this.repository.regenerateTranslation({
        storyId,
        sourceLanguage,
        targetLanguage: targetLang,
        scope,
        // Forwarded only for partial scope — these are phrases[] positions (the
        // repo + backend index space). Today the only caller is the full-scope
        // "Regenerate" button, which passes none.
        phraseIndices: scope === 'partial' ? phraseIndices : undefined,
        handler: options.handler,
        signal: options.signal,
        instruction: options.instruction,
        storyContext: this.storyContext ?? undefined,
      });
      const data = response.translation;
      runInAction(() => {
        if (superseded()) return;
        this.translations.set(targetLang, captureMachineOriginals(data));
        if (scope === 'full') {
          // Full regenerate rebuilds the entire phrase set — pre-existing
          // pending edits no longer match any phrase and would silently
          // stop applying. Drop them so the view state is honest.
          this.pendingEdits.delete(targetLang);
        }
        this.loadingStatus.set(targetLang, 'loaded');
      });
      this.logger.info('regenerateTranslation: completed', {
        targetLang,
        scope,
        phraseCount: data.phrases?.length,
      });
    } catch (error) {
      if (isAbortError(error)) {
        runInAction(() => {
          if (superseded()) return;
          // Restore to 'loaded' only when a prior translation was cached
          // (the common case — regenerate is only reachable with a language
          // selected). If the cache is empty we'd otherwise leave the UI
          // stuck on the spinner shape; 'idle' gets it back to the toggle.
          const restored: TranslationLoadingStatus = this.translations.has(
            targetLang,
          )
            ? 'loaded'
            : 'idle';
          this.loadingStatus.set(targetLang, restored);
        });
        return;
      }
      runInAction(() => {
        if (superseded()) return;
        this.loadingStatus.set(targetLang, 'error');
      });
      this.logger.error('regenerateTranslation failed', {
        targetLang,
        scope,
        handlerId: options.handler?.id,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * PLAT-10258 — whole-translation directed re-translate (D13). One backend
   * call — `regenerateTranslation(lang, 'full')` with the free-text
   * `instruction` (the stateful PLAT-10256 op persists the rebased result and
   * records the instruction in the changelog server-side) — wrapped in client
   * orchestration over the reviewer's fixes:
   *
   * - **Fresh rebase** (`keepFixes: false`): regenerate, nothing else — the
   *   full-scope install already swaps the cached data (overrides gone) and
   *   clears pending edits. The confirm dialog's discard count is the
   *   caller's concern, via `collectUserFixes` BEFORE calling this.
   * - **Keep my fixes** (`keepFixes: true`): snapshot the fixes via
   *   `collectUserFixes` BEFORE the destructive rebuild, regenerate, then
   *   re-apply each fix onto the fresh phrase set via
   *   `createOverrideSegment`, re-anchored by its source `originalIndices`
   *   (a re-translate does not change source indices). Segment fixes replay
   *   before standalone single edits (`collectUserFixes` ordering) so the
   *   stronger unit wins when the rebased tiling merges them.
   *
   * Every re-apply forces `origin: 'directed'`: a forced origin always
   * authors a real override segment, so `null` reliably means "could not
   * re-attach" — the default `'manual'` origin would DELEGATE a ≤1-fragment
   * span to `editTranslatedPhrase` (returning `null` on success and skipping
   * the overlap guard), making success indistinguishable from failure here.
   *
   * A fix that cannot re-attach (its source span no longer resolves against
   * the rebased tiling, or it collides with an already re-applied fix) lands
   * in `unresolved` — flagged for the caller's post-run summary, never
   * silently dropped (SPEC invariant). A regenerate failure re-throws before
   * any fix is touched; the prior translation stays cached.
   */
  async directedRetranslateWhole(
    lang: string,
    options: {
      instruction: string;
      keepFixes: boolean;
      /**
       * PLAT-10258 (BUG-5) — optional cancel signal, forwarded to the
       * regenerate. An aborted regenerate returns WITHOUT replacing the cache,
       * so after the await we re-check `signal.aborted` and bail. Otherwise
       * keep-mode would re-apply the snapshotted fixes onto the STALE (un-
       * regenerated) translation, flag every one unresolved, and let the caller
       * fire a false success event. No caller wires a signal today (the modal
       * disables Cancel in-flight), so this is a forward-compatible guard.
       */
      signal?: AbortSignal;
    },
  ): Promise<DirectedRetranslateResult> {
    const { instruction, keepFixes, signal } = options;
    // A regenerate that was cancelled resolves without throwing and without
    // touching the cache; treat that as the cancellation it is.
    const throwIfAborted = () => {
      if (signal?.aborted) {
        throw new DOMException('directed re-translate aborted', 'AbortError');
      }
    };
    if (!keepFixes) {
      await this.regenerateTranslation(lang, 'full', undefined, {
        instruction,
        signal,
      });
      throwIfAborted();
      return { reapplied: [], unresolved: [] };
    }

    // Snapshot BEFORE the rebuild — the full-scope install replaces the
    // cached phrase set wholesale, so this is the only record of the fixes.
    const prior = this.translations.get(lang);
    const fixes = prior
      ? collectUserFixes(prior.phrases, prior.parentPhrases ?? [])
      : [];

    await this.regenerateTranslation(lang, 'full', undefined, {
      instruction,
      signal,
    });
    // Bail BEFORE re-applying: if the regenerate was cancelled, the cache still
    // holds the OLD translation (with these very fixes) — re-applying here would
    // flag every fix unresolved against stale data and fire a false success.
    throwIfAborted();

    const reapplied: CollectedUserFix[] = [];
    const unresolved: CollectedUserFix[] = [];
    for (const fix of fixes) {
      let segmentId: string | null = null;
      try {
        segmentId = await this.createOverrideSegment(
          lang,
          fix.originalIndices,
          fix.translatedValue,
          {
            origin: 'directed',
            rejectExpansion: true,
            // PLAT-10258 × PLAT-10611 — re-anchor by DURABLE identity, not the
            // ephemeral integer position PLAT-10611 demoted. When the fix carries
            // stable ids the covered filter + expansion guard match by UUID, so a
            // kept fix survives a transcript edit that shifted its integer slot;
            // undefined here (legacy fix) keeps the integer re-anchor.
            coveredElementIds: fix.originalElementIds,
          },
        );
      } catch (error) {
        // The DESIGN's named failure mode: the rebased tiling no longer
        // covers any of the fix's source indices, so the override build
        // rejects the empty span. Treat exactly like a `null` — flag it.
        this.logger.warn('directedRetranslateWhole: fix failed to re-apply', {
          lang,
          originalIndices: fix.originalIndices,
          ...extractErrorInfo(error),
        });
      }
      (segmentId !== null ? reapplied : unresolved).push(fix);
    }

    this.logger.info('directedRetranslateWhole: completed', {
      lang,
      keepFixes,
      fixCount: fixes.length,
      reappliedCount: reapplied.length,
      unresolvedCount: unresolved.length,
    });
    return { reapplied, unresolved };
  }

  /**
   * PLAT-10046 — composition-only retranslate. Dispatched from the
   * `CompositionQuickEditPopover` footer ("Translate composition" /
   * "Retranslate composition") and reused by PLAT-9969's auto-trigger for
   * add-after-translation gap-fill.
   *
   * Routes to the existing Regenerate endpoint with `scope: 'compositions'`.
   * Backend uses `compositionTargets` for LLM context and merges results
   * against `existingCompositionPhrases`. `force: true` discards AI phrase
   * translations + pinned manual edits matching the optional `sourceTexts`
   * (or every composition phrase if `sourceTexts` is omitted); preset-locked
   * rows are untouched per PLAT-9963.
   *
   * Caller-driven `compositionTargets` — matches the established
   * `generateTranslation` contract so this store stays surface-agnostic
   * (clip-preview's per-clip `VideoSourceStore` differs from the main
   * `rootStore.videoSourceStore`; the caller knows which one is in scope).
   *
   * **Does NOT touch `loadingStatus`.** The transcript-side `'generating'`
   * status surfaces in the language-picker UI as a spinner; for a per-
   * composition retranslate that would over-broadcast. The popover owns
   * its own in-flight spinner state.
   *
   * On success, swaps `translations[lang]` via `captureMachineOriginals`
   * (parity with `generateTranslation`'s install path). The replacement
   * invalidates `_compositionPhraseLookupMemo` automatically and the
   * PLAT-9966 projection picks up the new phrases on the next paint.
   *
   * On error (including abort), re-throws after logging; caller decides
   * how to surface (popover renders an inline error pill in v1).
   */
  async retranslateCompositionPhrases(args: {
    lang: string;
    compositionTargets: TranslatableTarget[];
    force: boolean;
    /** PLAT-9965 variant B telemetry tag — backend logs but still operates on full uncovered set. */
    compositionElementId?: string;
    /** PLAT-9965 variant C telemetry tag — backend restricts force to these sourceTexts when supplied. */
    sourceTexts?: string[];
    /** PLAT-10112 variant-D advisory tag — standalone text-overlay retranslate (`kind: 'text'` target). */
    textElementId?: string;
    /**
     * The showcase the user is translating in (PLAT-10875). Same contract as
     * `generateTranslation`: the store can't reach the legacy VideoCreatorStore,
     * so the React caller supplies it (`videoCreator.organization?.id`). The
     * composition regenerate path loads translation memory backend-side, so
     * without it a story in several showcases gets the wrong workspace's
     * glossary. Validated backend-side.
     */
    showcaseId?: string;
    signal?: AbortSignal;
  }): Promise<{ compositions?: CompositionTranslationOutcome }> {
    const {
      lang,
      compositionTargets,
      force,
      compositionElementId,
      sourceTexts,
      textElementId,
      showcaseId,
      signal,
    } = args;
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.retranslateCompositionPhrases: no storyId on RootStore',
      );
    }
    // PLAT-10506 — fail closed when there is nothing to retranslate. A field
    // the popover surfaces as translatable but the extractor can't resolve to a
    // target (e.g. a control-less composition whose text lives on a nested leaf
    // the top-level text walk doesn't reach — full coverage is PLAT-10112)
    // would otherwise dispatch an empty `compositionTargets`, which the backend
    // rejects with a 400. Skip the round trip and no-op instead.
    if (compositionTargets.length === 0) {
      this.logger.warn(
        'retranslateCompositionPhrases: no targets to retranslate, skipping',
        { lang, compositionElementId, textElementId },
      );
      return { compositions: undefined };
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;
    const existingCompositionPhrases =
      this.translations.get(lang)?.compositionPhrases;

    const superseded = this.watchCacheGeneration();
    this.logger.info('retranslateCompositionPhrases: starting', {
      storyId,
      sourceLanguage,
      lang,
      force,
      compositionTargetCount: compositionTargets.length,
      sourceTextsCount: sourceTexts?.length ?? 0,
      compositionElementId,
      textElementId,
    });
    try {
      const { translation, compositions } =
        await this.repository.regenerateTranslation({
          storyId,
          showcaseId,
          sourceLanguage,
          targetLanguage: lang,
          scope: 'compositions',
          signal,
          storyContext: this.storyContext ?? undefined,
          compositionTargets,
          force,
          ...(existingCompositionPhrases ? { existingCompositionPhrases } : {}),
          ...(compositionElementId ? { compositionElementId } : {}),
          ...(sourceTexts ? { sourceTexts } : {}),
          ...(textElementId ? { textElementId } : {}),
        });
      runInAction(() => {
        if (superseded()) return;
        // Swap the entire cache entry — `captureMachineOriginals` stamps
        // the read-only baseline used by undo-edit detection. The cache
        // replacement invalidates `_compositionPhraseLookupMemo` (keyed on
        // the `TranslationData` reference) so the projection picks up the
        // new phrases on next paint.
        this.translations.set(lang, captureMachineOriginals(translation));
      });
      this.logger.info('retranslateCompositionPhrases: completed', {
        lang,
        compositionsStatus: compositions?.status,
        phrasesAdded:
          compositions?.status === 'ok' ? compositions.phrasesAdded : undefined,
        phrasesSkippedPinned:
          compositions?.status === 'ok'
            ? compositions.phrasesSkipped.pinned
            : undefined,
      });
      return { compositions };
    } catch (error) {
      if (isAbortError(error)) {
        this.logger.info('retranslateCompositionPhrases: aborted', { lang });
        throw error;
      }
      this.logger.error('retranslateCompositionPhrases failed', {
        lang,
        force,
        compositionTargetCount: compositionTargets.length,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * PLAT-10046 — upsert a composition phrase translation as a manual pin.
   *
   * Looks up the entry by `normalizePhrase(sourceText)`. When found, updates
   * `translatedValue` and sets `pinnedOverrideByManualEdit: true`. When not
   * found (the user is overriding a preset, or saving the first translation
   * for a sourceText that wasn't bulk-translated yet), creates a fresh
   * entry with the supplied `references[]` so future bulk runs know which
   * placements share this sourceText.
   *
   * Story-wide semantics: every placement of the same normalized sourceText
   * picks up the new translation on the next renderer projection sync
   * (handled by PLAT-9966's `projectCompositionTranslations`).
   *
   * Persistence is immediate (not coalesced with the transcript-edit
   * debounce) so a manual composition edit survives a refresh.
   */
  upsertCompositionPhraseTranslation(
    lang: string,
    args: {
      sourceText: string;
      translatedValue: string;
      references: CompositionPhraseTranslation['references'];
      /** Display name of the editor — used by the popover's pin-detail card. */
      pinnedBy?: string;
      /** ISO timestamp; defaults to now when omitted. */
      pinnedAt?: string;
      /**
       * PLAT-10046 — the translated value that was visible to the user
       * BEFORE this override. The caller (popover) computes this from the
       * resolver: for a `'preset'` row it's the preset value; for a
       * `'phrase'` row it's left undefined because the existing entry's
       * own `translatedValue` is already the right baseline. Snapshotted
       * into `originalTranslatedValue` on first pin so revert/pin-detail
       * always has somewhere to go even when the bulk-translate run
       * never produced an entry for this sourceText.
       */
      previousTranslatedValue?: string;
    },
  ): void {
    const data = this.translations.get(lang);
    if (!data) {
      this.logger.warn(
        'upsertCompositionPhraseTranslation: no translation cached for language; ignoring',
        { lang },
      );
      return;
    }
    // `pinnedBy` is a display-only label sourced from
    // `UserIdentityStore.currentEditor` (which falls back to "Unknown
    // editor" when no Clerk / Dato user is resolved). The popover never
    // makes authorization decisions off it; the backend remains the
    // source of truth for write permissions. Persisting whatever the
    // surface hands in is intentional — no validation here.
    const { sourceText, translatedValue, references, pinnedBy } = args;
    if (typeof sourceText !== 'string' || sourceText.trim() === '') return;
    if (typeof translatedValue !== 'string') return;
    const pinnedAt = args.pinnedAt ?? new Date().toISOString();
    const explicitPrevious =
      typeof args.previousTranslatedValue === 'string'
        ? args.previousTranslatedValue
        : undefined;

    const key = normalizePhrase(sourceText);
    const existing = Array.isArray(data.compositionPhrases)
      ? data.compositionPhrases
      : [];
    let matched = false;
    let matchedReferenceCount = 0;
    const nextPhrases: CompositionPhraseTranslation[] = existing.map(entry => {
      if (!entry || typeof entry.sourceText !== 'string') return entry;
      if (normalizePhrase(entry.sourceText) !== key) return entry;
      matched = true;
      matchedReferenceCount = entry.references?.length ?? 0;
      // PLAT-10046 — snapshot the pre-override translated value the first
      // time we pin so the popover can show "what was here before". Once
      // captured, preserve it across subsequent edits of the same pin so
      // the user can always revert (mentally or via the Revert button) to
      // the AI baseline.
      // Snapshot priority on the write:
      //   1. Existing snapshot — always preserved once captured.
      //   2. First-pin (entry not yet pinned) → `explicitPrevious` if
      //      supplied (preset baseline from the popover), else
      //      `entry.translatedValue` (the AI baseline we're overriding).
      //   3. Re-pin of a legacy entry already pinned with no snapshot
      //      → `explicitPrevious` only. **Never** snapshot
      //      `entry.translatedValue` here — it's the user's PRIOR
      //      override, not the AI baseline. Capturing it would cause
      //      Revert to bounce between two user values instead of
      //      returning to AI. Leaving it `undefined` causes the
      //      pin-detail card to fall back to source text (with the
      //      "legacy pin" comment); the user can hit Retranslate to
      //      repopulate an AI baseline if they need one.
      const originalTranslatedValue =
        entry.originalTranslatedValue !== undefined
          ? entry.originalTranslatedValue
          : entry.pinnedOverrideByManualEdit
            ? explicitPrevious
            : (explicitPrevious ?? entry.translatedValue);
      return {
        ...entry,
        translatedValue,
        pinnedOverrideByManualEdit: true,
        pinnedAt,
        ...(pinnedBy ? { pinnedBy } : {}),
        ...(originalTranslatedValue !== undefined
          ? { originalTranslatedValue }
          : {}),
        // Preserve the backend-managed references[] when the entry already
        // exists; the bulk pipeline refreshes them on the next run.
      };
    });
    if (!matched) {
      nextPhrases.push({
        sourceText,
        translatedValue,
        pinnedOverrideByManualEdit: true,
        references: references ?? [],
        pinnedAt,
        ...(pinnedBy ? { pinnedBy } : {}),
        // PLAT-10046 — for a brand-new entry (no prior bulk run for this
        // sourceText, typically a preset row), the caller passes the
        // preset value so revert has somewhere to land.
        ...(explicitPrevious !== undefined
          ? { originalTranslatedValue: explicitPrevious }
          : {}),
      });
    }

    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        compositionPhrases: nextPhrases,
        modifiedAt: new Date().toISOString(),
      });
    });
    void this.saveTranslationData(lang);
    this.logger.info('upsertCompositionPhraseTranslation: pinned', {
      lang,
      sourceTextLength: sourceText.length,
      created: !matched,
      referenceCount: matched
        ? matchedReferenceCount
        : (references?.length ?? 0),
    });
  }

  /**
   * PLAT-10046 — clear a manual phrase pin. Restores the entry's
   * `translatedValue` to the snapshot we captured on first pin
   * (`originalTranslatedValue` — the AI / preset baseline) and clears
   * `pinnedOverrideByManualEdit` + pin metadata. The entry stays in the
   * cache so the projection keeps using the AI value; the user can hit
   * Retranslate to refresh from a fresh LLM run if they want.
   *
   * Falls back to deleting the entry when no `originalTranslatedValue`
   * snapshot is present (legacy pins, or brand-new pins that overrode
   * nothing) — then the projection cascades to preset → original-
   * language and the user can Retranslate to get an AI value.
   *
   * No-ops when no cache entry exists, when the language has no cache,
   * or when the matching entry isn't `pinnedOverrideByManualEdit: true`
   * (we never touch AI-generated entries — only pins).
   */
  removeCompositionPhrasePin(lang: string, sourceText: string): void {
    const data = this.translations.get(lang);
    if (!data) return;
    if (typeof sourceText !== 'string' || sourceText.trim() === '') return;
    const existing = Array.isArray(data.compositionPhrases)
      ? data.compositionPhrases
      : [];
    if (existing.length === 0) return;
    const key = normalizePhrase(sourceText);
    let touched = false;
    const nextPhrases: CompositionPhraseTranslation[] = [];
    for (const entry of existing) {
      if (!entry || typeof entry.sourceText !== 'string') {
        nextPhrases.push(entry);
        continue;
      }
      if (normalizePhrase(entry.sourceText) !== key) {
        nextPhrases.push(entry);
        continue;
      }
      if (!entry.pinnedOverrideByManualEdit) {
        nextPhrases.push(entry);
        continue;
      }
      touched = true;
      // Restore to the AI baseline when we have a snapshot. Drop the
      // entry entirely when we don't (legacy or brand-new pin) so the
      // projection cascades to preset → original.
      if (typeof entry.originalTranslatedValue === 'string') {
        // Rest-spread strip pin metadata in one pass — the entry is no
        // longer a pin, so `pinnedAt`/`pinnedBy`/`originalTranslatedValue`
        // are stale. Avoids the `delete` casts the old shape required.
        const {
          pinnedAt: _pinnedAt,
          pinnedBy: _pinnedBy,
          originalTranslatedValue,
          ...rest
        } = entry;
        nextPhrases.push({
          ...rest,
          translatedValue: originalTranslatedValue,
          pinnedOverrideByManualEdit: false,
        });
      }
      // else: drop the entry (no baseline to restore to).
    }
    if (!touched) return;
    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        compositionPhrases: nextPhrases,
        modifiedAt: new Date().toISOString(),
      });
    });
    void this.saveTranslationData(lang);
    this.logger.info('removeCompositionPhrasePin: reverted', {
      lang,
      sourceTextLength: sourceText.length,
    });
  }

  /**
   * Destructive deletion of a language's translation. The server archives
   * `original.json` + `latest.json` to S3 before deleting the live blobs;
   * this method evicts the local cache entry afterwards and fires
   * `onLanguageRemoved` so each consuming view can fall its own selection
   * back to the source language.
   *
   * Backend 404 (`null` from the repository) is treated as success — the
   * desired end state is reached, so the eviction path runs identically.
   * This makes multi-tab races idempotent.
   *
   * Other errors leave the cache untouched and re-throw so the caller can
   * surface a toast.
   */
  async removeLanguage(targetLang: string): Promise<void> {
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.removeLanguage: no storyId on RootStore',
      );
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;
    const superseded = this.watchCacheGeneration();
    this.logger.info('removeLanguage: starting', { storyId, targetLang });

    // Cancel pending debounced saves for this language. We're about to
    // evict the cache entry; any trailing edit would write back stale
    // state and (worse) recreate the blob we're trying to remove.
    const debounced = this._debouncedSaves.get(targetLang);
    if (debounced) {
      debounced.cancel();
      this._debouncedSaves.delete(targetLang);
    }

    // Wait for any save already in flight to settle BEFORE the DELETE.
    // Two reasons:
    //   1. Local: the in-flight save's success branch writes
    //      `translations.set(lang, mergedData)` — running after our
    //      eviction would re-create the cache entry post-Remove and the
    //      dropdown would surface the language again.
    //   2. Server: a save PUT landing after our DELETE would re-create
    //      the live blob, defeating the archive-first invariant.
    // We swallow save failures here — the user is removing the language
    // anyway, so a failed trailing edit isn't worth a toast.
    const inflight = this._inflightSaves.get(targetLang);
    if (inflight) {
      try {
        await inflight;
      } catch {
        // ignore — eviction is the user's intent regardless
      }
    }

    try {
      await this.repository.removeTranslation({
        storyId,
        targetLanguage: targetLang,
        sourceLanguage,
      });
      // Both the 200 path and the null-from-404 path land here — eviction
      // is identical.
      if (superseded()) {
        // The story switched while the DELETE was open. The reset already
        // emptied the cache, and evicting `targetLang` now (plus firing the
        // event) would reset the INCOMING story's language selection.
        this.logger.info(
          'removeLanguage: removed, local eviction skipped — cache moved on',
          { targetLang },
        );
        return;
      }
      runInAction(() => {
        this.translations.delete(targetLang);
        this.pendingEdits.delete(targetLang);
        this.loadingStatus.delete(targetLang);
      });
      this.fireLanguageRemoved(targetLang);
      this.logger.info('removeLanguage: completed', { targetLang });
    } catch (error) {
      this.logger.error('removeLanguage failed', {
        targetLang,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Subscribe to language-removal events. The handler runs synchronously
   * inside the same MobX action that evicts the cache, so views can
   * capture cursor state / reset selection before the next render. Returns
   * an unsubscribe function for use in `useEffect` cleanup.
   */
  onLanguageRemoved(handler: (language: string) => void): () => void {
    this._languageRemovedListeners.add(handler);
    return () => {
      this._languageRemovedListeners.delete(handler);
    };
  }

  private fireLanguageRemoved(language: string): void {
    for (const handler of this._languageRemovedListeners) {
      try {
        handler(language);
      } catch (error) {
        this.logger.error('onLanguageRemoved handler threw', {
          language,
          ...extractErrorInfo(error),
        });
      }
    }
  }

  // --- Save state (PLAT-11649) ---------------------------------------------

  /** True while at least one persist for `lang` is in flight. */
  isSaving(lang: string): boolean {
    return (this.savingLanguages.get(lang) ?? 0) > 0;
  }

  /**
   * True while `lang` has edits the debounced save has not yet sent. Together
   * with `isSaving` this is "the stored translation is behind what is on
   * screen" — what a reader that must act on SAVED text (the automatic
   * re-dub, PLAT-11678) waits out.
   */
  hasPendingEdits(lang: string): boolean {
    return (this.pendingEdits.get(lang)?.size ?? 0) > 0;
  }

  /** Epoch ms of the last persist that landed for `lang`, if any. */
  lastSavedAtFor(lang: string): number | undefined {
    return this.lastSavedAt.get(lang);
  }

  /**
   * Subscribe to save failures. Fires for BOTH persist paths, including the
   * one that otherwise swallows its error. Returns an unsubscribe function
   * for `useEffect` cleanup.
   */
  onSaveFailed(
    handler: (language: string, error: unknown) => void,
  ): () => void {
    this._saveFailedListeners.add(handler);
    return () => {
      this._saveFailedListeners.delete(handler);
    };
  }

  private fireSaveFailed(language: string, error: unknown): void {
    for (const handler of this._saveFailedListeners) {
      try {
        handler(language, error);
      } catch (handlerError) {
        this.logger.error('onSaveFailed handler threw', {
          language,
          ...extractErrorInfo(handlerError),
        });
      }
    }
  }

  /**
   * Wrap one persist so the save state follows it: count it in flight, stamp
   * `lastSavedAt` when it lands, tell listeners when it fails. Rethrows, so
   * each caller keeps its own swallow/propagate contract. The decrement is in
   * `finally`, or a stale "Saving…" would stick after a failure or a story
   * switch — and the stamp rides along in that same action, so a reader
   * reacting to both (the header's "Saved", PLAT-11649) sees the landing and
   * the settle in one fire.
   */
  private async trackPersist(
    lang: string,
    run: () => Promise<unknown>,
  ): Promise<void> {
    runInAction(() => {
      this.savingLanguages.set(lang, (this.savingLanguages.get(lang) ?? 0) + 1);
    });
    let landedAt: number | undefined;
    try {
      await run();
      landedAt = Date.now();
    } catch (error) {
      this.fireSaveFailed(lang, error);
      throw error;
    } finally {
      runInAction(() => {
        if (landedAt !== undefined) this.lastSavedAt.set(lang, landedAt);
        const remaining = (this.savingLanguages.get(lang) ?? 1) - 1;
        if (remaining <= 0) this.savingLanguages.delete(lang);
        else this.savingLanguages.set(lang, remaining);
      });
    }
  }

  // --- Edits --------------------------------------------------------------

  editTranslatedPhrase(
    lang: string,
    originalIndices: number[],
    value: string,
  ): void {
    const hash = hashOriginalIndices(originalIndices);
    const data = this.translations.get(lang);
    const existing = this.pendingEdits.get(lang);
    const next = existing ? new Map(existing) : new Map<string, string>();
    next.set(hash, value);
    // PLAT-10611 (F4) — MIRROR the pending edit under the stable-element-id
    // identity key too, so the id-only identity readers (render hooks + editor
    // popover, via `getTranslatedPhraseByElementIds` / `hasPendingEditByElementIds`)
    // resolve it. The integer key above still serves the caption + the
    // persistence path (`saveEdits`), which stay on the integer basis — so no
    // edit is ever silently dropped. Written only when the target phrase carries
    // element ids; an id-less legacy phrase keeps the integer key only.
    const identityElementIds = data?.phrases.find(
      p => hashOriginalIndices(p.originalIndices) === hash,
    )?.originalElementIds;
    if (identityElementIds && identityElementIds.length > 0) {
      next.set(hashOriginalElementIds(identityElementIds), value);
    }
    this.pendingEdits.set(lang, next);

    // Optimistically fold the edit into the cached phrase so every view
    // bound to `translatedValue` (the inline translated transcript reads it
    // directly via useTranslatedRender) reflects the change immediately,
    // without waiting ~2s for the debounced network save to round-trip. The
    // debounced `saveEdits` below still persists it to S3; on the next
    // render the pending value and the cached value already agree.
    //
    // Capture the machine original on the FIRST edit, before overwriting
    // translatedValue. Idempotent: only captures when no original is yet
    // recorded AND the phrase isn't already user-edited (in which case the
    // stored translatedValue is itself a prior user edit, not machine
    // output, so we can't claim it's the original). Without this, the phrase
    // would lose Revert capability the moment the user edits — the post-save
    // phrase has `isUserEdited: true` and no captured original.
    if (data) {
      const targetIndex = data.phrases.findIndex(
        p => hashOriginalIndices(p.originalIndices) === hash,
      );
      const target = targetIndex >= 0 ? data.phrases[targetIndex] : undefined;
      // Mirror saveEdits: a no-op edit (value unchanged) must not flip a
      // machine phrase to user-edited (nor log an audit entry).
      // Whitespace-insensitive: a phantom-space or no-op re-type must not count
      // as a change (inter-word spacing is reconstructed at render, not stored).
      // The RAW `value` is still what gets stored below — only the gate changes.
      const valueChanged = target
        ? normalizeForCompare(value) !==
          normalizeForCompare(target.translatedValue)
        : false;
      let mutated = false;
      const updatedPhrases = data.phrases.map(p => {
        if (hashOriginalIndices(p.originalIndices) !== hash) return p;
        mutated = true;
        // Read translatedValue (machine output, since !isUserEdited) BEFORE
        // overwriting it below to capture the original safely.
        const captureOriginal =
          p.originalTranslatedValue === undefined && !p.isUserEdited;
        return {
          ...p,
          ...(captureOriginal
            ? { originalTranslatedValue: p.translatedValue }
            : {}),
          // Attribute the edit to the current reviewer, but only on a real
          // change (a no-op re-type must not stamp — same gate as isUserEdited).
          ...(valueChanged ? { editedBy: this.currentEditedBy() } : {}),
          translatedValue: value,
          isUserEdited: valueChanged ? true : p.isUserEdited,
        };
      });
      if (mutated) {
        this.translations.set(lang, {
          ...data,
          phrases: updatedPhrases,
          // Audit entry only on a real change (a no-op re-type must not log).
          ...(valueChanged && target
            ? {
                changelog: this.withChange(data, {
                  phraseIndex: targetIndex,
                  type: 'manual_edit',
                  previousValue: target.translatedValue,
                  newValue: value,
                  previousSnapshot: target.originalValueSnapshot,
                  newSnapshot: target.originalValueSnapshot,
                }),
              }
            : {}),
        });
      }
    }

    this.getDebouncedSave(lang)();
  }

  /**
   * PLAT-10852 — durably link fragments to the translation-memory entry a
   * reviewer just promoted them into.
   *
   * The backend stamps `tmEntryIds` only when it re-translates, so a promote
   * alone left the link in the review's in-memory session map: the entry
   * persisted, but after a refresh the fragment read as "not an entry" again
   * and every surface (edit overlay, Changes / TM cards, the "TM (N)" badge,
   * the term underline) went back to offering "Save to Translation Memory" for
   * a term the reviewer had already saved. Writing the id onto the phrase makes
   * the link survive the reload, exactly like a re-translate's provenance.
   *
   * The caller resolves WHICH phrases the promoted unit covers (a plain
   * fragment is one phrase; an override segment is all of its derived
   * fragments) and passes their `originalIndices`. Additive and idempotent:
   * an id already present is left alone, other ids are kept, and no matching
   * phrase means no write and no save. Returns whether anything changed.
   */
  async attachTmEntryToPhrases(
    lang: string,
    originalIndicesList: readonly (readonly number[])[],
    entryId: string,
  ): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data || originalIndicesList.length === 0) return false;

    const targets = new Set(
      originalIndicesList.map(indices => hashOriginalIndices([...indices])),
    );
    let mutated = false;
    const phrases = data.phrases.map(p => {
      if (!targets.has(hashOriginalIndices(p.originalIndices))) return p;
      if (p.tmEntryIds?.includes(entryId)) return p;
      mutated = true;
      return { ...p, tmEntryIds: [...(p.tmEntryIds ?? []), entryId] };
    });
    if (!mutated) {
      this.logger.warn('attachTmEntryToPhrases: no phrase matched', {
        lang,
        entryId,
        targetCount: targets.size,
      });
      return false;
    }

    this.translations.set(lang, { ...data, phrases });
    this.logger.info('attachTmEntryToPhrases: linked', {
      lang,
      entryId,
      phraseCount: targets.size,
    });
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * Append a reviewer-action entry to the changelog and return the new array
   * (callers spread it into the next `TranslationData`). PLAT-10259 provenance:
   * the backend save is a verbatim writer, so the plugin owns changelog
   * composition — without this, the append-only audit log the retire-not-delete
   * model exists for stays empty. Stamps `datetime` + the translation's
   * `handler`; the caller supplies the action-specific fields.
   */
  private withChange(
    data: TranslationData,
    change: Omit<TranslationChange, 'datetime' | 'handler'>,
  ): TranslationChange[] {
    return [
      ...data.changelog,
      { ...change, datetime: new Date().toISOString(), handler: data.handler },
    ];
  }

  // --- Reversible override segments (D10, PLAT-10214) ---------------------
  //
  // A reviewer replaces a contiguous run of ≥2 TRANSLATED fragments with one
  // typed correction. Rather than destroying the originals, we RETIRE them
  // (`active: false`, `supersededBy` → the override id) and overlay a manual
  // (or directed, T14) override segment whose own fragments render in their
  // place. One undo reactivates the originals — restoring the EXACT machine
  // tiling + values. Single-fragment edits stay on `editTranslatedPhrase`.

  /**
   * Create a reversible override segment over the covered active fragments
   * (epic D10). `coveredOriginalIndices` is the union of the run's source
   * indices (what the selection resolver produced).
   *
   * Delegation: when the span covers ≤1 active fragment AND `origin` is the
   * default `'manual'`, this is just a single-phrase edit — delegate to
   * `editTranslatedPhrase` so the single-word path behaves EXACTLY as today
   * (debounced save, capture-original, `isUserEdited`). A forced `origin`
   * (`'directed'`, T14) always authors a segment even for one fragment, so the
   * provenance is recorded.
   *
   * Multi-fragment (or forced-origin): build the override via
   * `buildOverrideSegment`, swap the covered originals for their retired copies,
   * insert the segment into `parentPhrases[]`, and insert the override's own
   * fragment(s) into `phrases[]` — either a SINGLE active fragment
   * (`fragment: false`, or no `fragmenter` available) or the splitter's tiling
   * (`fragment: true` + a `fragmenter`, the T9 path). Stale pending edits for
   * the covered indices are dropped. Saves IMMEDIATELY (a structural change,
   * not a debounced text edit).
   *
   * `fragmenter` is injected (not a hard repository call) so the store stays a
   * pure cache + ops layer and unit-tests without a network: the workspace
   * passes the real T9 splitter call; tests pass a deterministic fake; absence
   * falls back to a single fragment (also the T12 inline shape).
   *
   * Returns the created segment id (or `null` when it delegated to a
   * single-phrase edit) so the caller can track / revert it.
   */
  async createOverrideSegment(
    lang: string,
    coveredOriginalIndices: number[],
    value: string,
    options: {
      fragment?: boolean;
      origin?: OverrideOrigin;
      fragmenter?: SegmentFragmenter;
      /**
       * PLAT-10258 (BUG-3, Denys's call 2026-07-05: flag-unresolved) — refuse to
       * author the override if the covered active phrases span MORE source
       * indices than `coveredOriginalIndices` asked for (the re-translation
       * regrouped the fix's words into a larger phrase). Used by the directed-
       * re-translate re-apply so a fix can't silently swallow — and overwrite the
       * fresh translation of — its neighbor words. Returns null → the caller
       * lists it as unresolved.
       */
      rejectExpansion?: boolean;
      // PLAT-10611 (F5, F4-carry) — the covered content words' stable element
      // ids (from the selection's `data-source-uuid`). When present the covered
      // filter matches by identity FIRST (drift-correct on a reordered clip),
      // then falls back to the positional index intersection for id-less
      // whitespace members / legacy blobs.
      coveredElementIds?: string[];
      /**
       * PLAT-10257 (A2) — the reviewer's steering text for a DIRECTED span
       * re-translate. Recorded verbatim on the changelog entry
       * (`TranslationChange.instruction`) so the Changes panel can label the
       * fix as machine-steered with its instruction (A5). Only the store can
       * record it: this op composes the changelog entry internally via
       * `withChange`. Manual span replaces omit it — nothing changes for them.
       */
      instruction?: string;
      /**
       * PLAT-10266 (Card 7, §D) — RE-ANCHOR the authored override's source
       * coverage to these element ids instead of the covered phrases' own ids.
       * The vanished-source re-translate covers a STALE phrase that still points
       * at a source token "Replace Term" DELETED — authoring off its coverage
       * (the default) would key the new override to that dead id, so it re-orphans
       * from the shared render hook. The re-translate resolves the replacement's
       * live added tokens and passes their fresh ids here so the authored segment
       * (and its derived fragments) render by identity (`source_uuid ?? id`). The
       * COVERED FILTER still uses `coveredElementIds` / `coveredOriginalIndices` to
       * find + retire the stale phrase; ONLY the authored coverage is re-anchored.
       * Absent → unchanged behavior.
       */
      reanchorAuthoredElementIds?: string[];
    } = {},
  ): Promise<string | null> {
    const {
      fragment = false,
      origin = 'manual',
      fragmenter,
      rejectExpansion = false,
      coveredElementIds,
      instruction,
      reanchorAuthoredElementIds,
    } = options;
    const data = this.translations.get(lang);
    if (!data) {
      this.logger.warn('createOverrideSegment: no translation cached', {
        lang,
      });
      return null;
    }

    const coveredSet = new Set(coveredOriginalIndices);
    const coveredIdSet = new Set(coveredElementIds ?? []);
    // Is this fix anchored on durable identity? (Some `coveredElementIds` given.)
    const idAnchored = coveredIdSet.size > 0;
    // The covered ACTIVE fragments (a retired fragment is never re-covered) —
    // see `coveredActivePhrases` for the identity-first rule.
    const covered = this.coveredActivePhrases(
      data,
      coveredOriginalIndices,
      coveredElementIds,
    );

    // PLAT-10258 (BUG-3) — a directed re-apply must not EXPAND. `buildOverride
    // Segment` spans the UNION of every covered phrase, so if the re-translation
    // regrouped the fix's source words into a larger phrase, authoring the
    // override here would overwrite the freshly-translated neighbor words with
    // the old fix text — and still report success. When the resulting span
    // would reach source indices the fix never claimed, bail out and let the
    // caller flag the fix unresolved (DESIGN: "re-apply where they still fit;
    // list the ones that no longer fit"; Denys's call 2026-07-05).
    if (rejectExpansion && covered.length > 0) {
      // PLAT-10258 × PLAT-10611: judge expansion on the SAME axis the fix was
      // anchored on. When the fix is identity-anchored, compare the covered set's
      // stable ids against the fix's claimed ids — an integer-span comparison
      // would misfire after a position shift (the fixed word's fresh ephemeral
      // index is not in the fix's stale integer set, so a correct re-anchor would
      // look like an expansion). Id-less covered members (whitespace) carry no
      // identity and so cannot expand the id-claimed span. Legacy/id-less fixes
      // keep the integer-span check unchanged.
      let expandsBeyondFix: boolean;
      let resultSpan: readonly (string | number)[];
      if (idAnchored) {
        const resultIds = unionOriginalElementIds(covered);
        resultSpan = resultIds;
        expandsBeyondFix = resultIds.some(id => !coveredIdSet.has(id));
      } else {
        const resultIndices = unionOriginalIndices(covered);
        resultSpan = resultIndices;
        expandsBeyondFix = resultIndices.some(idx => !coveredSet.has(idx));
      }
      if (expandsBeyondFix) {
        this.logger.warn(
          'createOverrideSegment: re-apply would expand beyond the fix span (re-translation regrouped the words) — flagged unresolved, fresh translation preserved',
          {
            lang,
            fixSpan: idAnchored ? coveredElementIds : coveredOriginalIndices,
            resultSpan,
          },
        );
        return null;
      }
    }

    // ≤1 covered fragment + default origin → a plain single-phrase edit.
    if (covered.length <= 1 && origin === 'manual') {
      const target = covered[0];
      const indices = target ? target.originalIndices : coveredOriginalIndices;
      this.editTranslatedPhrase(lang, indices, value);
      return null;
    }

    // Guard: a span may NOT overlap an existing override (decision 2026-06-17 —
    // see the plan doc "Decision … guard overlapping overrides"). A selection
    // entirely within one override is routed to `updateOverrideSegment`
    // upstream; reaching here while touching an override means the span spills
    // beyond it. Allowing it would NEST overrides — colliding the `o_<min>` id
    // (double-coverage corruption on revert), forcing multi-level undo, and
    // leaving dangling parents. Reject; the reviewer reverts the existing
    // override first. To support nesting instead, follow the plan-doc decision's
    // "If this is to change" steps (unique id + undo model + dangling cleanup).
    const existingParents = data.parentPhrases ?? [];
    const overlapsOverride = covered.some(c => {
      const parent = existingParents.find(s => s.id === c.parentId);
      if (!parent) return false;
      // Identify an override by its `o_` id prefix, not `origin` alone — `origin`
      // is optional and may be absent on a legacy/back-end blob, but the id
      // scheme is authoritative. (Keying on origin alone let an origin-less
      // override slip past, producing a duplicate `o_<min>` id on re-edit.)
      return (
        parent.id.startsWith('o_') ||
        parent.origin === 'manual' ||
        parent.origin === 'directed'
      );
    });
    if (overlapsOverride) {
      this.logger.warn(
        'createOverrideSegment: span overlaps an existing override — rejected (revert it first)',
        { lang, coveredCount: covered.length },
      );
      return null;
    }

    const { segment, retired } = buildOverrideSegment(covered, value, origin);
    // PLAT-10266 (Card 7, §D) — RE-ANCHOR the authored coverage to the live
    // replacement tokens' fresh ids when the covered span vanished (see the
    // option doc). `buildOverrideSegment` derives coverage from the covered
    // (stale) phrases, which still carry the deleted token's dead id; overwrite
    // just the segment's `originalElementIds` so it — and the fragments derived
    // from it below — render by identity and do not re-orphan. The integer
    // `originalIndices` (the lead / ordering key) is left as the covered span so
    // the override still leads where the replaced word sat.
    if (reanchorAuthoredElementIds && reanchorAuthoredElementIds.length > 0) {
      segment.originalElementIds = [...reanchorAuthoredElementIds];
    }
    // The covered fragments in source order — the changelog records their text
    // as the value this override replaced.
    const orderedCovered = covered
      .slice()
      .sort((a, b) => phraseLead(a) - phraseLead(b));

    return this.commitOverrideWrite(lang, data, {
      label: 'createOverrideSegment',
      segment,
      retired,
      authoredOver: covered,
      survivingParents: existingParents,
      dropPendingEditsFor: covered,
      previousValue: joinPhraseValues(
        orderedCovered.map(c => c.translatedValue),
      ),
      newValue: value,
      origin,
      // PLAT-10257 (A2): the steering text rides the changelog entry verbatim
      // (A5 renders it); undefined for unsteered (manual) authoring.
      instruction,
      fragment,
      fragmenter,
      successMessage: 'createOverrideSegment: created',
      successFields: {
        origin,
        coveredCount: covered.length,
        fragmented: fragment && !!fragmenter,
      },
    });
  }

  /**
   * Re-edit an existing manual/directed override segment (epic D10 re-edit). A
   * manual segment's `translatedValue` is authoritative; its fragments are
   * always a derived split of it. So overriding any fragment (or span) WITHIN
   * the segment updates the WHOLE segment's words: replace its
   * `translatedValue`, drop its current derived fragments, and re-derive. The
   * segment's source relation (`originalIndices` / `originalValueSnapshot`) and
   * the RETIRED pre-override originals are untouched — so no matter how many
   * times it is re-edited, one undo still reaches the original machine
   * translation. Saves immediately. Reused by T14 for directed segments.
   *
   * No-op (returns `false`) when the segment id isn't a known override segment.
   */
  async updateOverrideSegment(
    lang: string,
    segmentId: string,
    value: string,
    options: { fragment?: boolean; fragmenter?: SegmentFragmenter } = {},
  ): Promise<boolean> {
    const { fragment = false, fragmenter } = options;
    const data = this.translations.get(lang);
    if (!data) return false;
    const parents = data.parentPhrases ?? [];
    const existing = parents.find(s => s.id === segmentId);
    if (!existing) {
      this.logger.warn('updateOverrideSegment: unknown segment', {
        lang,
        segmentId,
      });
      return false;
    }

    // The segment record with its text updated (source relation unchanged).
    // Re-attribute to the current reviewer (PLAT-10444 Changes tab).
    const updatedSegment: ParentPhrase = {
      ...existing,
      translatedValue: value,
      editedBy: this.currentEditedBy(),
    };
    const nextParents = parents.map(s =>
      s.id === segmentId ? updatedSegment : s,
    );

    const superseded = this.watchCacheGeneration();
    const derived = await this.deriveOverrideFragments(
      updatedSegment,
      fragment ? fragmenter : undefined,
    );
    if (superseded()) {
      this.logger.info('updateOverrideSegment: abandoned, cache moved on', {
        lang,
        segmentId,
      });
      return false;
    }

    // Drop the segment's CURRENT derived fragments (active fragments whose
    // parentId is this segment) — but KEEP the retired originals
    // (`active: false`, same parentId is fine; they're matched by `active`).
    const withoutDerived = data.phrases.filter(
      p => !(p.parentId === segmentId && p.active !== false),
    );
    const nextPhrases = [...withoutDerived, ...derived];

    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        parentPhrases: nextParents,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: segmentId,
          type: existing.origin === 'directed' ? 'retranslate' : 'manual_edit',
          previousValue: existing.translatedValue,
          newValue: value,
          previousSnapshot: existing.originalValueSnapshot ?? '',
          newSnapshot: existing.originalValueSnapshot ?? '',
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info('updateOverrideSegment: re-edited', {
      lang,
      segmentId,
      derivedCount: derived.length,
    });
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * PLAT-11655 — correct a span that OVERLAPS existing corrections without
   * undoing them first. The corrections the span touches are ABSORBED into the
   * new one, as ONE record rewrite and ONE save.
   *
   * The no-nesting rule `createOverrideSegment` guards still holds: the new
   * correction REPLACES the ones it touched rather than sitting on top of them.
   * What the resulting records are, and the data-safety invariants that force
   * this to be a single write, are decided by `planAbsorb` in
   * `services/transcript/translationAbsorbPlan.ts`; this method performs the
   * write that plan asks for and saves.
   *
   * Delegation, because the cached translation can move between the reviewer
   * opening the editor and confirming: a span that turns out to touch NO
   * correction is a plain create, and one that sits ENTIRELY inside a single
   * correction is a re-edit of it. Either way the typed text lands rather than
   * silently vanishing.
   *
   * `origin` is accepted for parity with `createOverrideSegment`, but only the
   * manual path calls this. A directed (machine-steered) re-translate still
   * refuses any touching span: absorbing one would overwrite part of a
   * reviewer's typed correction with machine text.
   *
   * Returns the new segment id, or null when nothing was authored — including
   * the refusal of a correction that retired no machine original, which the
   * caller surfaces to the reviewer.
   */
  async absorbIntoOverrideSegment(
    lang: string,
    coveredOriginalIndices: number[],
    value: string,
    options: {
      fragment?: boolean;
      origin?: OverrideOrigin;
      fragmenter?: SegmentFragmenter;
      /** PLAT-10611 — the covered content words' stable ids (identity-first
       *  covered filter, integer fallback for id-less members). */
      coveredElementIds?: string[];
      /** Recorded verbatim on the changelog entry when `origin` is directed. */
      instruction?: string;
    } = {},
  ): Promise<string | null> {
    const {
      fragment = false,
      origin = 'manual',
      fragmenter,
      coveredElementIds,
      instruction,
    } = options;
    const data = this.translations.get(lang);
    if (!data) {
      this.logger.warn('absorbIntoOverrideSegment: no translation cached', {
        lang,
      });
      return null;
    }

    const covered = this.coveredActivePhrases(
      data,
      coveredOriginalIndices,
      coveredElementIds,
    );
    // The decision itself is a pure transformation of the cached records; this
    // store only performs the write it asks for (CLAUDE.md layer rule: stores
    // hold observable state, heavy algorithms live outside them).
    const plan = planAbsorb(
      data.phrases,
      data.parentPhrases ?? [],
      covered,
      value,
      origin,
    );

    if (plan.kind === 'nothing-covered') {
      this.logger.warn('absorbIntoOverrideSegment: nothing covered', {
        lang,
        coveredOriginalIndices,
      });
      return null;
    }

    // Nothing to absorb ⇒ a plain fresh create (the guard there is moot).
    if (plan.kind === 'plain-create') {
      this.logger.info(
        'absorbIntoOverrideSegment: span touches no correction — plain create',
        { lang },
      );
      return this.createOverrideSegment(lang, coveredOriginalIndices, value, {
        fragment,
        origin,
        fragmenter,
        coveredElementIds,
        instruction,
      });
    }

    // Entirely inside ONE correction ⇒ the D10 re-edit, with the composed value
    // (the reviewer re-typed part of it; the rest survives around it).
    if (plan.kind === 're-edit') {
      this.logger.info(
        'absorbIntoOverrideSegment: span sits inside one correction — re-edit',
        { lang, segmentId: plan.segmentId },
      );
      const ok = await this.updateOverrideSegment(
        lang,
        plan.segmentId,
        plan.composedValue,
        { fragment, fragmenter },
      );
      return ok ? plan.segmentId : null;
    }

    if (plan.kind === 'unrestorable') {
      this.logger.warn(
        'absorbIntoOverrideSegment: absorbed correction retired no machine originals — refused (undo could not restore it)',
        { lang, unrestorable: plan.unrestorable },
      );
      return null;
    }

    const {
      absorbedIds,
      composedValue,
      segment,
      retired,
      authoredOver,
      survivingParents,
      previousActive,
    } = plan;

    // The absorbed parents leave in this SAME write, so the shared write's
    // collision backstop is given the surviving parents only — a span starting
    // on an absorbed correction's first word legitimately re-mints its id — and
    // their still-active child fragments are dropped as the write rebuilds
    // `phrases[]`.
    return this.commitOverrideWrite(lang, data, {
      label: 'absorbIntoOverrideSegment',
      segment,
      retired,
      authoredOver,
      survivingParents,
      dropActiveChildrenOf: new Set(absorbedIds),
      dropPendingEditsFor: previousActive,
      previousValue: joinFragments(previousActive),
      newValue: composedValue,
      origin,
      instruction,
      fragment,
      fragmenter,
      successMessage: 'absorbIntoOverrideSegment: absorbed',
      successFields: {
        absorbed: absorbedIds,
        origin,
        coveredCount: covered.length,
      },
    });
  }

  /**
   * The one write `createOverrideSegment` and `absorbIntoOverrideSegment` both
   * finish with: derive the override's own fragments, swap each authored-over
   * fragment for its retired copy in place, insert the override, drop the
   * pending edits it invalidated, and write + save in a single transaction.
   *
   * One write and one save is not a convenience — a half-applied override
   * leaves source words with no active text and no way to undo.
   *
   * `authoredOver` pairs positionally with `retired` once both are in source
   * order, which is the order `buildOverrideSegment` produced `retired` in.
   *
   * Returns the new override's id, or null when it refused to write.
   */
  private async commitOverrideWrite(
    lang: string,
    data: TranslationData,
    params: {
      /** Method name for the log lines, so a refusal says which path refused. */
      label: string;
      segment: ParentPhrase;
      retired: readonly TranslationPhrase[];
      authoredOver: readonly TranslationPhrase[];
      /** Parent records that survive this write; the new one is appended. */
      survivingParents: readonly ParentPhrase[];
      /** Parent ids whose still-active derived fragments leave (absorb only). */
      dropActiveChildrenOf?: ReadonlySet<string>;
      dropPendingEditsFor: readonly TranslationPhrase[];
      previousValue: string;
      newValue: string;
      origin: OverrideOrigin;
      instruction?: string;
      fragment: boolean;
      fragmenter?: SegmentFragmenter;
      successMessage: string;
      successFields: Record<string, unknown>;
    },
  ): Promise<string | null> {
    const {
      label,
      segment,
      retired,
      authoredOver,
      survivingParents,
      dropActiveChildrenOf,
      dropPendingEditsFor,
      previousValue,
      newValue,
      origin,
      instruction,
      fragment,
      fragmenter,
      successMessage,
      successFields,
    } = params;

    // Attribute the override to the current reviewer (PLAT-10444 Changes tab).
    segment.editedBy = this.currentEditedBy();

    // Backstop against id collision: never append a second segment with an id
    // already present (would yield two `o_<idx>` parents → `revertOverrideSegment`
    // reactivates BOTH sets of retired originals → double coverage).
    if (survivingParents.some(s => s.id === segment.id)) {
      this.logger.warn(
        `${label}: segment id already exists — rejected (would duplicate parentId)`,
        { lang, segmentId: segment.id },
      );
      return null;
    }

    // Derive the override's own fragments. With the splitter (T9) we tile the
    // override text into word-level fragments; without it (or fragment:false)
    // the override is one active fragment over the whole span. Either way each
    // derived fragment carries `parentId` → the segment, `isUserEdited: true`,
    // and `active` (default true). Whitespace 1:1-shape: a single fragment
    // preserves it trivially (it owns the same source span); the splitter is
    // responsible for peeling boundary whitespace when it runs (T9 / PLAT-9618).
    const superseded = this.watchCacheGeneration();
    const derived = await this.deriveOverrideFragments(
      segment,
      fragment ? fragmenter : undefined,
    );
    if (superseded()) {
      // `data` was read before the await; writing it back now would resurrect
      // the outgoing story's blob in the incoming story's cache.
      this.logger.info(`${label}: abandoned, cache moved on`, {
        lang,
        segmentId: segment.id,
      });
      return null;
    }

    // Map each authored-over fragment (by object identity) to its retired copy.
    const orderedAuthored = authoredOver
      .slice()
      .sort((a, b) => phraseLead(a) - phraseLead(b));
    const retiredFor = new Map<TranslationPhrase, TranslationPhrase>();
    orderedAuthored.forEach((c, i) => retiredFor.set(c, retired[i]));

    // Rebuild `phrases[]`: drop the absorbed corrections' derived fragments,
    // replace each authored-over original IN PLACE with its retired copy
    // (array order preserved so a later collapse/undo restores the same
    // tiling), then append the override's own derived fragments.
    const surviving = dropActiveChildrenOf
      ? data.phrases.filter(
          p =>
            !(
              p.parentId &&
              dropActiveChildrenOf.has(p.parentId) &&
              p.active !== false
            ),
        )
      : data.phrases;
    const nextPhrases: TranslationPhrase[] = surviving.map(
      p => retiredFor.get(p) ?? p,
    );
    nextPhrases.push(...derived);

    const nextParents = [...survivingParents, segment];

    // Drop stale pending edits whose phrase just stopped being active — their
    // hash no longer matches an active fragment, so they would silently stop
    // applying.
    this.dropPendingEditsForIndices(lang, dropPendingEditsFor);

    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        parentPhrases: nextParents,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: segment.id,
          type: origin === 'directed' ? 'retranslate' : 'manual_edit',
          previousValue,
          newValue,
          previousSnapshot: segment.originalValueSnapshot ?? '',
          newSnapshot: segment.originalValueSnapshot ?? '',
          instruction,
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info(successMessage, {
      lang,
      segmentId: segment.id,
      ...successFields,
      derivedCount: derived.length,
    });
    await this.saveTranslationData(lang);
    return segment.id;
  }

  /**
   * One-click full undo of an override segment (epic D10, step 4). Drops the
   * segment from `parentPhrases[]` and ALL its derived fragments from
   * `phrases[]`, then reactivates the originals it retired (`active: true`,
   * clearing `supersededBy`) — restoring the exact original tiling + values.
   * Saves immediately. Reused by T14 (directed) and T6.5 (per-phrase undo of a
   * segment). No-op (returns `false`) for an unknown segment.
   */
  async revertOverrideSegment(
    lang: string,
    segmentId: string,
  ): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) return false;
    const parents = data.parentPhrases ?? [];
    if (!parents.some(s => s.id === segmentId)) {
      this.logger.warn('revertOverrideSegment: unknown segment', {
        lang,
        segmentId,
      });
      return false;
    }

    const reverted = parents.find(s => s.id === segmentId);
    // The originals this segment retired (still `active: false` here) — their
    // machine text is what the revert restores; capture before reactivation.
    const restoredOriginals = data.phrases.filter(
      p => p.supersededBy === segmentId,
    );

    const nextParents = parents.filter(s => s.id !== segmentId);
    const nextPhrases = data.phrases
      // Drop the override's derived fragments (its active children).
      .filter(p => !(p.parentId === segmentId && p.active !== false))
      // Reactivate the originals this segment retired.
      .map(p => {
        if (p.supersededBy !== segmentId) return p;
        const { supersededBy: _supersededBy, ...rest } = p;
        return { ...rest, active: true };
      });

    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        // Keep the key present (even if empty) so consumers don't see a
        // type flip; an empty array is the honest "no segments" state.
        parentPhrases: nextParents,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: segmentId,
          type: 'manual_edit',
          previousValue: reverted?.translatedValue ?? '',
          newValue: joinPhraseValues(
            restoredOriginals.map(p => p.translatedValue),
          ),
          previousSnapshot: reverted?.originalValueSnapshot ?? '',
          newSnapshot: restoredOriginals
            .map(p => p.originalValueSnapshot ?? '')
            .join(' '),
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info('revertOverrideSegment: reverted', { lang, segmentId });
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * Fine-split a just-authored override segment via the on-demand splitter
   * (PLAT-10269 / T9). The endpoint is SAVE-BASED: it re-splits the SAVED
   * translation server-side (writes latest.json, bumps version, appends `split`
   * changelog), so we POST it then REFETCH the authoritative result and adopt
   * it. `phraseIndices` are POSITIONS into the CURRENT `phrases[]` — valid only
   * because `createOverrideSegment`/`updateOverrideSegment` already saved (so the
   * in-memory and stored arrays agree).
   *
   * BEST-EFFORT — and usually a NO-OP for manual overrides (decision 2026-06-18,
   * verified live). The backend splitter (PLAT-9605, `openAISentenceSplitter`) is
   * an LLM caption sub-divider that aligns each sub-piece back to the parent's
   * SOURCE words and preserves the phrase whole on any alignment/validation
   * failure (`segmentsSplit: 0`). A manual override is a free rewrite whose text
   * need not correspond to the source words it covers, so the splitter typically
   * can't produce a valid source-aligned subdivision and leaves it as one
   * fragment. We keep the call anyway: it DOES fine-split the occasional override
   * that stays close to the machine text, and it NEVER corrupts (failure ⇒ the
   * single fragment is kept — valid and reversible, just coarse). True override
   * re-splitting (a non-source-aligned text splitter) is separate backend work;
   * per-fragment linkage INSIDE one correction is a documented non-goal
   * (PLAT-10287). Also a no-op when the backend is absent/old or rejects bounds.
   */
  async resplitOverrideSegment(lang: string, segmentId: string): Promise<void> {
    const data = this.translations.get(lang);
    if (!data) return;
    const storyId = this.rootStore.storyId;
    if (!storyId) return;
    const phraseIndices: number[] = [];
    data.phrases.forEach((p, i) => {
      if (p.parentId === segmentId && p.active !== false) phraseIndices.push(i);
    });
    if (phraseIndices.length === 0) return;
    const sourceLanguage = this.rootStore.transcriptionStore.language;
    const superseded = this.watchCacheGeneration();
    try {
      await this.repository.splitTranslation({
        storyId,
        sourceLanguage,
        targetLanguage: lang,
        scope: 'segment',
        phraseIndices,
      });
      // Refetch the authoritative post-split translation (the server bumped
      // version + appended `split` changelog; a partial merge would go stale).
      // When the splitter no-ops the server skips the write, so this returns the
      // unchanged blob — adopting it is harmless.
      const fresh = await this.repository.fetchTranslationByLanguage(
        storyId,
        lang,
      );
      if (fresh && !superseded()) {
        runInAction(() => {
          this.translations.set(lang, captureMachineOriginals(fresh));
        });
        this.logger.info('resplitOverrideSegment: re-split', {
          lang,
          segmentId,
          phraseCount: phraseIndices.length,
        });
      }
    } catch (error) {
      this.logger.warn(
        'resplitOverrideSegment: split failed — override kept as a single fragment',
        { lang, segmentId, ...extractErrorInfo(error) },
      );
    }
  }

  // --- Split controls + Backspace recombine (T10, PLAT-10270) --------------
  //
  // Lead-keyed tiling ops for the Translation Review workspace: the coverage
  // popover and the Backspace gesture identify a segment by a fragment's LEAD
  // (its smallest source `initial_index` — the `data-initial-index` the DOM
  // stamps), so each op first resolves lead → segment via `getSegments`.
  // Split (whole + per-segment) is backend-driven (the PLAT-10269 splitter,
  // POST→refetch→adopt — same idiom as `resplitOverrideSegment`); collapse /
  // reapply / merge are the pure `translationTiling` transforms persisted
  // through the verbatim Save (one writer, no backend call).

  /** Resolve the segment whose active fragments include the one with this
   *  lead. Fragments are references into `phrases[]` (`getSegments`), so
   *  identity-based position lookups against `data.phrases` stay valid. */
  private findSegmentById(
    lang: string,
    segmentId: string,
  ): TranslationSegment | undefined {
    return this.getSegments(lang).find(s => s.id === segmentId);
  }

  /** Find the segment that CONTAINS the active fragment with this numeric lead.
   *  The recombine (Backspace) path addresses one fragment by its position,
   *  not a segment by its stable id — so it keeps the by-lead lookup. */
  private findSegmentByLead(
    lang: string,
    lead: number,
  ): TranslationSegment | undefined {
    return this.getSegments(lang).find(s =>
      s.fragments.some(f => phraseLead(f) === lead),
    );
  }

  /** Whether the segment with this stable id is currently SPLIT (tiled by >1
   *  active fragment). False when no data / no segment. */
  isSegmentSplit(lang: string, segmentId: string): boolean {
    const segment = this.findSegmentById(lang, segmentId);
    return segment ? translationTiling.isSegmentSplit(segment) : false;
  }

  /** Whether a collapsed split is CACHED for the segment with this stable id
   *  (retired fragments without `supersededBy` — see
   *  `translationTiling.hasCachedSplit`). False when no data / segment. */
  hasCachedSegmentSplit(lang: string, segmentId: string): boolean {
    const data = this.translations.get(lang);
    if (!data) return false;
    const segment = this.findSegmentById(lang, segmentId);
    return segment
      ? translationTiling.hasCachedSplit(data.phrases, segment.id)
      : false;
  }

  /**
   * "Split into Words" — fine-split the WHOLE translation via the
   * on-demand splitter (PLAT-10269, `scope: 'whole'`). SAVE-BASED endpoint:
   * save first so the stored and in-memory `phrases[]` agree, POST, then
   * refetch the authoritative result and adopt it wholesale (the exact
   * `resplitOverrideSegment` idiom). Returns `false` (translation unchanged)
   * when no data is cached or the POST/refetch fails.
   */
  async splitWholeTranslation(lang: string): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) {
      this.logger.warn('splitWholeTranslation: no translation cached', {
        lang,
      });
      return false;
    }
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      this.logger.warn('splitWholeTranslation: no storyId on RootStore', {
        lang,
      });
      return false;
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;
    const superseded = this.watchCacheGeneration();
    try {
      // The endpoint re-splits the SAVED blob — persist the in-memory state
      // first so the two agree (the positional contract, doc comment on
      // `resplitOverrideSegment`).
      await this.saveTranslationData(lang);
      await this.repository.splitTranslation({
        storyId,
        sourceLanguage,
        targetLanguage: lang,
        scope: 'whole',
      });
      const fresh = await this.repository.fetchTranslationByLanguage(
        storyId,
        lang,
      );
      if (fresh && !superseded()) {
        runInAction(() => {
          this.translations.set(lang, captureMachineOriginals(fresh));
        });
      }
      this.logger.info('splitWholeTranslation: split', { lang });
      // TODO(PLAT-10271): emit trackTranslationReview_SegmentSplit(storyId, language, scope, action)
      return true;
    } catch (error) {
      this.logger.warn(
        'splitWholeTranslation: split failed — translation unchanged',
        { lang, ...extractErrorInfo(error) },
      );
      return false;
    }
  }

  /**
   * Per-segment Split (coverage popover) — fine-split ONE segment via the
   * on-demand splitter (`scope: 'segment'`). `phraseIndices` are POSITIONS
   * into the current `phrases[]`, computed by identity (the segment's
   * fragments are references into `phrases[]`), and are valid server-side
   * because we save first. No-op `false` when the segment id resolves to no
   * segment or the segment is already split.
   */
  async splitSegment(lang: string, segmentId: string): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) {
      this.logger.warn('splitSegment: no translation cached', {
        lang,
        segmentId,
      });
      return false;
    }
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      this.logger.warn('splitSegment: no storyId on RootStore', { lang });
      return false;
    }
    const segment = this.findSegmentById(lang, segmentId);
    if (!segment || translationTiling.isSegmentSplit(segment)) {
      this.logger.warn('splitSegment: no unsplit segment for id', {
        lang,
        segmentId,
      });
      return false;
    }
    const fragmentSet = new Set(segment.fragments);
    const phraseIndices: number[] = [];
    data.phrases.forEach((p, i) => {
      if (fragmentSet.has(p)) phraseIndices.push(i);
    });
    if (phraseIndices.length === 0) {
      this.logger.warn('splitSegment: fragments not found in phrases[]', {
        lang,
        segmentId: segment.id,
      });
      return false;
    }
    const sourceLanguage = this.rootStore.transcriptionStore.language;
    const superseded = this.watchCacheGeneration();
    try {
      // Save first — the positional `phraseIndices` contract (see
      // `resplitOverrideSegment`) requires stored and in-memory to agree.
      await this.saveTranslationData(lang);
      await this.repository.splitTranslation({
        storyId,
        sourceLanguage,
        targetLanguage: lang,
        scope: 'segment',
        phraseIndices,
      });
      const fresh = await this.repository.fetchTranslationByLanguage(
        storyId,
        lang,
      );
      if (fresh && !superseded()) {
        runInAction(() => {
          this.translations.set(lang, captureMachineOriginals(fresh));
        });
      }
      this.logger.info('splitSegment: split', {
        lang,
        segmentId: segment.id,
        phraseCount: phraseIndices.length,
      });
      // TODO(PLAT-10271): emit trackTranslationReview_SegmentSplit(storyId, language, scope, action)
      return true;
    } catch (error) {
      this.logger.warn('splitSegment: split failed — segment unchanged', {
        lang,
        segmentId: segment.id,
        ...extractErrorInfo(error),
      });
      return false;
    }
  }

  /**
   * Undo split (coverage popover) — collapse the split segment with this
   * stable id back to ONE coarse phrase, writing the redo cache (retired
   * fragments, no `supersededBy`). Pure transform + verbatim Save; no backend
   * call. `false` when the segment id resolves to no segment, the segment has
   * no `parent` record, or it isn't split.
   */
  async collapseSegmentSplit(
    lang: string,
    segmentId: string,
  ): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) return false;
    const segment = this.findSegmentById(lang, segmentId);
    const parent = segment?.parent;
    if (!segment || !parent) {
      this.logger.warn('collapseSegmentSplit: no split segment for id', {
        lang,
        segmentId,
      });
      return false;
    }
    const nextPhrases = translationTiling.collapseSegment(
      data.phrases,
      parent,
      segment.fragments,
    );
    if (!nextPhrases) return false;

    // Source text is unchanged by tiling — one snapshot for both sides.
    const snapshot = segment.fragments
      .map(f => f.originalValueSnapshot ?? '')
      .join(' ');
    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: parent.id,
          type: 'split',
          previousValue: joinPhraseValues(
            segment.fragments.map(f => f.translatedValue),
          ),
          newValue: parent.translatedValue,
          previousSnapshot: snapshot,
          newSnapshot: snapshot,
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info('collapseSegmentSplit: collapsed', {
      lang,
      segmentId: parent.id,
    });
    // TODO(PLAT-10271): emit trackTranslationReview_SegmentSplit(storyId, language, scope, action)
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * Redo split (coverage popover) — reactivate the cached tiling written by
   * `collapseSegmentSplit`. Strictly replay-from-cache (the popover's
   * `canRedoSplit = hasCachedSplit && !isSplit`); an edit made on the
   * collapsed coarse phrase is discarded (accepted v1 rule — the changelog
   * records both states). `false` when there is no cache or the segment is
   * currently split.
   */
  async reapplySegmentSplit(lang: string, segmentId: string): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) return false;
    const segment = this.findSegmentById(lang, segmentId);
    if (!segment) {
      this.logger.warn('reapplySegmentSplit: no segment for id', {
        lang,
        segmentId,
      });
      return false;
    }
    const nextPhrases = translationTiling.reapplySegmentSplit(
      data.phrases,
      segment.id,
    );
    if (!nextPhrases) return false;

    // The cache entries this reapply reactivates (in source order).
    const reactivated = data.phrases
      .filter(
        p =>
          p.parentId === segment.id &&
          p.active === false &&
          p.supersededBy === undefined,
      )
      .sort((a, b) => phraseLead(a) - phraseLead(b));
    const snapshot = reactivated
      .map(f => f.originalValueSnapshot ?? '')
      .join(' ');
    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: segment.id,
          type: 'split',
          previousValue: joinPhraseValues(
            segment.fragments.map(f => f.translatedValue),
          ),
          newValue: joinPhraseValues(reactivated.map(f => f.translatedValue)),
          previousSnapshot: snapshot,
          newSnapshot: snapshot,
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info('reapplySegmentSplit: reapplied', {
      lang,
      segmentId: segment.id,
      fragmentCount: reactivated.length,
    });
    // TODO(PLAT-10271): emit trackTranslationReview_SegmentSplit(storyId, language, scope, action)
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * Backspace recombine — merge the active fragment with this lead into the
   * fragment BEFORE it in the same segment (never across a segment
   * boundary). Pure transform + verbatim Save; the merged phrase is
   * attributed to the current reviewer. `false` when the lead resolves to
   * no segment, is the segment's first fragment, or the merge is invalid.
   */
  async mergeFragmentIntoPrevious(
    lang: string,
    lead: number,
  ): Promise<boolean> {
    const data = this.translations.get(lang);
    if (!data) return false;
    const segment = this.findSegmentByLead(lang, lead);
    if (!segment) {
      this.logger.warn('mergeFragmentIntoPrevious: no segment for lead', {
        lang,
        lead,
      });
      return false;
    }
    const nextPhrases = translationTiling.mergeFragmentIntoPrevious(
      data.phrases,
      segment,
      lead,
      this.currentEditedBy(),
    );
    if (!nextPhrases) return false;

    // The merged phrase is the transform's one NEW object (text preserved by
    // a merge — previousValue === newValue in the audit entry).
    const before = new Set(data.phrases);
    const merged = nextPhrases.find(p => !before.has(p));
    const mergedValue = merged?.translatedValue ?? '';
    const mergedSnapshot = merged?.originalValueSnapshot ?? '';
    runInAction(() => {
      this.translations.set(lang, {
        ...data,
        phrases: nextPhrases,
        changelog: this.withChange(data, {
          phraseIndex: -1,
          parentId: segment.id,
          type: 'split',
          previousValue: mergedValue,
          newValue: mergedValue,
          previousSnapshot: mergedSnapshot,
          newSnapshot: mergedSnapshot,
        }),
        modifiedAt: new Date().toISOString(),
      });
    });
    this.logger.info('mergeFragmentIntoPrevious: merged', {
      lang,
      segmentId: segment.id,
      lead,
    });
    // TODO(PLAT-10271): emit trackTranslationReview_SegmentSplit(storyId, language, scope, action)
    await this.saveTranslationData(lang);
    return true;
  }

  /**
   * Span-level coverage check (PLAT-10268 / T8). A THIN pass-through: it
   * assembles the edited span pair from the current phrase + transcription data,
   * calls the repository, and RETURNS the verdict. It writes NOTHING to the
   * store — the Translation Review workspace owns the verdict in local state
   * keyed per fragment, never the store (D6). No `runInAction`, no changelog
   * entry, no persistence.
   *
   * Span-read recipe (amended 2026-07-21 for stable ids):
   * - `translatedSpan` = the ACTIVE covering phrases' `translatedValue`, joined
   *   in source order (`joinFragments` default pick); "covering" is stable-ID
   *   overlap on `originalElementIds`.
   * - `originalSpan` = the source elements' `.value` read BY stable id
   *   (`source_uuid ?? id`) from `transcriptionStore` (NOT
   *   `phrase.originalValueSnapshot`, which is empty for machine phrases / fresh
   *   overrides). Trim each, skip empties, join with `joinPhraseValues`.
   * - `coveredElementIds` are stable source-word ids (PLAT-10611), not indices;
   *   a phrase with no ids is a stale translation and yields no coverage.
   */
  async checkCoverage(
    lang: string,
    coveredElementIds: string[],
  ): Promise<CoverageVerdict> {
    const storyId = this.rootStore.storyId;
    // No story id ⇒ no check is possible. Degrade to the same non-blocking
    // `covered`/100 verdict the endpoint and `ensureCoverageVerdict` fall back
    // to, so a missing id can never gate a save (invariant F). Mirrors
    // `resplitOverrideSegment`'s `if (!storyId) return;` early-out, adapted to a
    // method that must return a verdict.
    if (!storyId) {
      return { coveragePercent: 100, status: 'covered', explanation: '' };
    }

    const data = this.translations.get(lang);

    // translatedSpan: join the active covering phrases' translatedValue, where
    // "covering" is stable-ID overlap (createOverrideSegment's covering idiom,
    // now on ids). A phrase with no ids never overlaps — a stale translation
    // yields no coverage.
    const coveredSet = new Set(coveredElementIds);
    const covered = (data?.phrases ?? []).filter(
      p =>
        p.active !== false &&
        (p.originalElementIds?.some(id => coveredSet.has(id)) ?? false),
    );
    const translatedSpan = joinFragments(covered);

    // originalSpan: source-element .value read BY stable id (source_uuid ?? id)
    // from the transcription store — drift-correct, unlike a positional read. On
    // a reordered / montage clip the position read pulls the wrong words; the id
    // read does not.
    const byElementId = new Map<string, string | null | undefined>();
    for (const el of this.rootStore.transcriptionStore.getFinalTranscriptionElements()) {
      const id = el.source_uuid ?? el.id;
      if (id !== undefined) byElementId.set(id, el.value);
    }
    const sourceParts: string[] = [];
    for (const id of coveredElementIds) {
      const text = byElementId.get(id)?.trim();
      if (text) sourceParts.push(text);
    }
    const originalSpan = joinPhraseValues(sourceParts);

    return this.repository.checkCoverage({
      storyId,
      sourceLanguage: this.rootStore.transcriptionStore.language,
      targetLanguage: lang,
      originalSpan,
      translatedSpan,
    });
  }

  /**
   * Directed re-translation of a single span (PLAT-10257 / A2). A THIN
   * pass-through like `checkCoverage` above: resolves the story + source-
   * language context, forwards the covered SOURCE elements plus the
   * reviewer's optional steering `instruction` to the stateless PLAT-10256
   * span endpoint via the repository, and RETURNS the re-translated string.
   * It writes NOTHING to the store — the caller
   * (`useSpanReplace.confirmDirected`) authors the reversible
   * `origin: 'directed'` override from the returned value, so a failure here
   * (thrown through, incl. the missing-storyId guard) can never corrupt
   * cached or stored data. Lives on the store because the layer rules keep
   * repository access out of components/hooks. `instruction` stays optional:
   * the stale re-translate path (PLAT-10266) reuses this with it omitted.
   */
  async directedRetranslateSpan(
    lang: string,
    elements: TranscriptElement[],
    instruction?: string,
  ): Promise<string> {
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error(
        'TranslationStore.directedRetranslateSpan: no storyId on RootStore',
      );
    }
    return this.repository.directedRetranslateSpan({
      storyId,
      sourceLanguage: this.rootStore.transcriptionStore.language,
      targetLanguage: lang,
      elements,
      instruction,
    });
  }

  /**
   * The ACTIVE fragments a span covers (a retired fragment is never
   * re-covered). Shared by `createOverrideSegment` and
   * `absorbIntoOverrideSegment` (PLAT-11655) so both author over the same set.
   *
   * PLAT-10611 (F5, F4-carry) + PLAT-10258 keep-fixes re-anchor: when the fix
   * is IDENTITY-anchored (some `coveredElementIds` given), the decision is
   * identity-DECISIVE PER PHRASE — a fragment that HAS stable ids is judged by
   * UUID ONLY, never by an integer fall-back. The old "UUID OR integer" test let
   * a DIFFERENT source word that merely slid into the fix's stale ephemeral slot
   * (a transcript edit between fix-capture and keep-fixes re-translate) get
   * wrongly covered by the integer branch. An id-LESS fragment (whitespace
   * member, legacy pre-UUID blob) still uses the positional `initial_index`
   * intersection so it is not leaked out of retirement — this is why an id-only
   * GLOBAL filter is unsafe (see §6). When no ids were supplied at all (the
   * common single-edit / span-edit callers), everything keeps the pure integer
   * behavior unchanged.
   */
  private coveredActivePhrases(
    data: TranslationData,
    coveredOriginalIndices: readonly number[],
    coveredElementIds: readonly string[] | undefined,
  ): TranslationPhrase[] {
    const coveredSet = new Set(coveredOriginalIndices);
    const coveredIdSet = new Set(coveredElementIds ?? []);
    const idAnchored = coveredIdSet.size > 0;
    return data.phrases.filter(p => {
      if (p.active === false) return false;
      if (
        idAnchored &&
        p.originalElementIds &&
        p.originalElementIds.length > 0
      ) {
        return p.originalElementIds.some(id => coveredIdSet.has(id));
      }
      return p.originalIndices.some(idx => coveredSet.has(idx));
    });
  }

  /**
   * Derive the override segment's own fragment(s) from its authoritative
   * `translatedValue`. With a `fragmenter` (the T9 splitter, injected) we tile
   * the text into word-level fragments; without one we produce a SINGLE active
   * fragment over the whole span (the fragment:false / splitter-unavailable
   * path, and the T12 inline shape). Every derived fragment carries
   * `parentId` → the segment, `isUserEdited: true`, `active: true`, and the
   * segment's `originalValueSnapshot` so coverage/staleness compare against the
   * live source. Falls back to the single fragment if the splitter throws (it
   * must never block the save).
   */
  private async deriveOverrideFragments(
    segment: ParentPhrase,
    fragmenter: SegmentFragmenter | undefined,
  ): Promise<TranslationPhrase[]> {
    // `originalValueSnapshot` is optional on ParentPhrase (back-end `p_` parents
    // omit it) but required on the derived fragments — coalesce once here.
    const snapshot = segment.originalValueSnapshot ?? '';
    const singleFragment = (): TranslationPhrase[] => [
      {
        originalIndices: [...segment.originalIndices],
        originalElementIds: segment.originalElementIds
          ? [...segment.originalElementIds]
          : undefined,
        translatedValue: segment.translatedValue,
        isUserEdited: true,
        originalValueSnapshot: snapshot,
        handler: this.handlerForLang(segment),
        parentId: segment.id,
        active: true,
      },
    ];
    if (!fragmenter) return singleFragment();
    try {
      const tiles = await fragmenter({
        segmentId: segment.id,
        translatedValue: segment.translatedValue,
        originalIndices: [...segment.originalIndices],
        originalValueSnapshot: snapshot,
      });
      if (!tiles || tiles.length === 0) return singleFragment();
      return tiles.map(t => ({
        originalIndices: t.originalIndices,
        // originalElementIds: t.originalElementIds, TODO: splitter to return originalElementIds
        translatedValue: t.translatedValue,
        isUserEdited: true,
        originalValueSnapshot: t.originalValueSnapshot ?? snapshot,
        handler: this.handlerForLang(segment),
        parentId: segment.id,
        active: true,
      }));
    } catch (error) {
      this.logger.warn(
        'deriveOverrideFragments: fragmenter failed; using single fragment',
        { segmentId: segment.id, ...extractErrorInfo(error) },
      );
      return singleFragment();
    }
  }

  /** The reviewer attribution for an edit, read from `UserIdentityStore`
   *  (PLAT-10444 "Changes" tab). Persisted on the edited phrase / override
   *  `ParentPhrase` so the Translation Review Changes tab can show who made each
   *  change. Email is omitted when unknown so the field stays minimal. */
  private currentEditedBy(): { name: string; email?: string; at: string } {
    const identity = this.rootStore.userIdentityStore;
    // `currentEditor` already returns 'Unknown' when no user is resolved; the
    // optional chain only guards a store that isn't wired (test mocks).
    const name = identity?.currentEditor ?? 'Unknown';
    const email = identity?.currentIdentity?.email;
    const at = new Date().toISOString();
    return email ? { name, email, at } : { name, at };
  }

  /** A handler stamp for a derived fragment — reuse the language's translation
   *  handler so the fragment is attributable; falls back to a synthetic manual
   *  handler when none is cached. */
  private handlerForLang(_segment: ParentPhrase): TranslationHandler {
    // Derived override fragments are reviewer-authored; attribute them to the
    // story's translation handler when present so provenance/audit stay intact.
    return { id: 'manual-override', provider: 'human' };
  }

  /** Drop pending edits whose phrase indices we just retired. Their hash no
   *  longer matches an active fragment, so they would silently stop applying. */
  private dropPendingEditsForIndices(
    lang: string,
    retiredPhrases: readonly TranslationPhrase[],
  ): void {
    const pending = this.pendingEdits.get(lang);
    if (!pending || pending.size === 0) return;
    const next = new Map(pending);
    for (const p of retiredPhrases) {
      next.delete(hashOriginalIndices(p.originalIndices));
      // PLAT-10611 (F4): also clear the mirrored stable-id key (see
      // `editTranslatedPhrase`) so a retired phrase's pending edit stops
      // applying on the id-only identity path too.
      if (p.originalElementIds && p.originalElementIds.length > 0) {
        next.delete(hashOriginalElementIds(p.originalElementIds));
      }
    }
    if (next.size === 0) this.pendingEdits.delete(lang);
    else this.pendingEdits.set(lang, next);
  }

  /**
   * Restore a phrase to its machine-generated original. Handles two cases:
   *
   *   1. `originalTranslatedValue` captured by the backend → drop any
   *      pending edit AND reset `translatedValue` + clear `isUserEdited`
   *      on the stored phrase. Pushes to S3 immediately so the reset
   *      isn't coalesced with a later edit by the debounce.
   *
   *   2. No captured original AND `!isUserEdited` → the stored value IS
   *      the machine original, so dropping the pending edit alone is
   *      sufficient. No S3 write needed (stored state is unchanged).
   *
   *   3. `isUserEdited: true` AND no captured original → we cannot
   *      recover the machine original client-side. No-op. UI gates this
   *      away via `resolveOriginalValue` returning undefined.
   */
  revertTranslation(lang: string, originalElementIds: string[]): void {
    const data = this.translations.get(lang);
    if (!data) return;
    // PLAT-10268 (stable-id migration): identify the phrase by its STABLE
    // element ids, not by positional integer. Id-ONLY — a phrase with no ids
    // (a stale, pre-PLAT-10611 translation) never matches and reverts to a
    // no-op (correct: coverage/revert are meaningful only once regenerated
    // with stable ids). No index fallback.
    const idHash = hashOriginalElementIds(originalElementIds);
    const phrase = data.phrases.find(
      p =>
        p.originalElementIds !== undefined &&
        p.originalElementIds.length > 0 &&
        hashOriginalElementIds(p.originalElementIds) === idHash,
    );
    if (!phrase) return;

    const hasCapturedOriginal = phrase.originalTranslatedValue !== undefined;
    if (!hasCapturedOriginal && phrase.isUserEdited) {
      // Case 3 — cannot recover original; UI should have prevented this.
      this.logger.warn(
        'revertTranslation: no originalTranslatedValue for an isUserEdited phrase; no-op',
        { lang },
      );
      return;
    }

    // Always drop any pending edit for this phrase. Revert is an explicit
    // discard of in-progress work. `editTranslatedPhrase` writes the pending
    // edit under BOTH the integer key and the stable-id key (PLAT-10611 F4), so
    // clear both: `idHash` (this method's identity) plus the integer key
    // derived from the matched phrase's own indices.
    const intHash = hashOriginalIndices(phrase.originalIndices);
    const existing = this.pendingEdits.get(lang);
    if (existing && (existing.has(idHash) || existing.has(intHash))) {
      const next = new Map(existing);
      next.delete(idHash);
      next.delete(intHash);
      if (next.size === 0) this.pendingEdits.delete(lang);
      else this.pendingEdits.set(lang, next);
    }

    if (hasCapturedOriginal) {
      // Case 1: rewrite the cached phrase in place and push to S3.
      const nextPhrases = data.phrases.map(p =>
        p.originalElementIds !== undefined &&
        p.originalElementIds.length > 0 &&
        hashOriginalElementIds(p.originalElementIds) === idHash
          ? {
              ...p,
              translatedValue: p.originalTranslatedValue ?? p.translatedValue,
              isUserEdited: false,
            }
          : p,
      );
      this.translations.set(lang, { ...data, phrases: nextPhrases });
      this.logger.info('revertTranslation: reverted to captured original', {
        lang,
        originalElementIds,
      });
      void this.saveTranslationData(lang);
    } else {
      // Case 2: stored state is already the machine original; dropping the
      // pending edit above is all that's needed.
      this.logger.info('revertTranslation: dropped pending edit', {
        lang,
        originalElementIds,
      });
    }
  }

  /**
   * Force a save of the current (merged) translation state for one
   * language. Unlike `saveEdits`, this does not require `pendingEdits` to
   * be non-empty — used by `revertTranslation` to push a reset to S3.
   *
   * Swallows errors after logging. The only caller (`revertTranslation`)
   * invokes this via `void` and doesn't await, so a re-throw would
   * become an unhandled promise rejection the UI never surfaces. Logging
   * is the observable signal; a follow-up ticket should surface save
   * failures in the popover UI explicitly.
   */
  private async saveTranslationData(lang: string): Promise<void> {
    const data = this.translations.get(lang);
    if (!data) return;
    const storyId = this.rootStore.storyId;
    if (!storyId) {
      this.logger.error('saveTranslationData: no storyId on RootStore', {
        lang,
      });
      return;
    }
    try {
      await this.trackPersist(lang, () =>
        this.repository.saveTranslation({
          storyId,
          targetLanguage: lang,
          translationData: data,
        }),
      );
    } catch (error) {
      this.logger.error('saveTranslationData failed', {
        lang,
        ...extractErrorInfo(error),
      });
    }
  }

  /**
   * Fold pendingEdits[lang] into translations[lang].phrases and POST to the
   * save endpoint. Called automatically by the debounced flush or explicitly
   * by consumers.
   */
  async saveEdits(lang: string): Promise<void> {
    const pending = this.pendingEdits.get(lang);
    const data = this.translations.get(lang);
    if (!pending || pending.size === 0 || !data) return;

    const storyId = this.rootStore.storyId;
    if (!storyId) {
      throw new Error('TranslationStore.saveEdits: no storyId on RootStore');
    }

    // Snapshot the exact key/value pairs we are about to persist so that any
    // edits made while the network request is in flight are preserved.
    const snapshot = new Map(pending);

    const mergedPhrases = data.phrases.map(p => {
      const hash = hashOriginalIndices(p.originalIndices);
      const pendingValue = snapshot.get(hash);
      if (pendingValue === undefined) return p;
      // Whitespace-insensitive gate (see editPhrase): the RAW pendingValue is
      // still persisted; only the user-edited flip is suppressed on a no-op.
      const valueChanged =
        normalizeForCompare(pendingValue) !==
        normalizeForCompare(p.translatedValue);
      return {
        ...p,
        translatedValue: pendingValue,
        isUserEdited: valueChanged ? true : p.isUserEdited,
      };
    });

    const mergedData: TranslationData = {
      ...data,
      phrases: mergedPhrases,
      modifiedAt: new Date().toISOString(),
    };

    const superseded = this.watchCacheGeneration();
    let run!: Promise<void>;
    run = (async () => {
      try {
        await this.trackPersist(lang, () =>
          this.repository.saveTranslation({
            storyId,
            targetLanguage: lang,
            translationData: mergedData,
          }),
        );
        runInAction(() => {
          // The POST landed for `storyId`, but the cache has since moved to
          // another story. Merging `mergedData` in now would put this story's
          // phrases under the new story's language — and the NEXT save reads
          // `storyId` fresh, so it would POST them to the new story's path,
          // overwriting a translation that was never edited (PLAT-11557).
          if (superseded()) return;
          this.translations.set(lang, mergedData);
          const current = this.pendingEdits.get(lang);
          if (!current) return;
          // Remove only the keys that were actually saved AND whose value
          // hasn't been overwritten during the save. Anything else is a fresh
          // edit the user made mid-flight and must survive.
          const remaining = new Map(current);
          for (const [key, savedValue] of snapshot) {
            if (remaining.get(key) === savedValue) remaining.delete(key);
          }
          if (remaining.size === 0) this.pendingEdits.delete(lang);
          else this.pendingEdits.set(lang, remaining);
        });
      } catch (error) {
        this.logger.error('saveEdits failed', {
          lang,
          ...extractErrorInfo(error),
        });
        throw error;
      } finally {
        // Guard: another overlapping save may have replaced our entry.
        if (this._inflightSaves.get(lang) === run) {
          this._inflightSaves.delete(lang);
        }
      }
    })();

    this._inflightSaves.set(lang, run);
    return run;
  }

  /**
   * Force any pending debounced saves to fire and await their completion.
   * Tests MUST call this before asserting post-edit state.
   */
  async flushChanges(): Promise<void> {
    for (const fn of this._debouncedSaves.values()) {
      fn.flush();
    }
    if (this._inflightSaves.size === 0) return;
    await Promise.all([...this._inflightSaves.values()]);
  }

  private getDebouncedSave(lang: string): DebouncedFunc<() => void> {
    const existing = this._debouncedSaves.get(lang);
    if (existing) return existing;
    const fn = debounce(() => {
      void this.saveEdits(lang);
    }, SAVE_DEBOUNCE_MS);
    this._debouncedSaves.set(lang, fn);
    return fn;
  }
}

function hashOriginalIndices(originalIndices: number[]): string {
  // Sort defensively: the data model treats originalIndices as canonical, but
  // a caller passing them unsorted would otherwise fail to match phrases.
  return [...originalIndices].sort((a, b) => a - b).join('-');
}

/**
 * PLAT-10611 (F4) — the stable-element-id identity hash, ADDED ALONGSIDE
 * `hashOriginalIndices` (never replacing it). Keys the id-only identity path
 * (edit / override / pending-edit reads) on a phrase's `originalElementIds`
 * (the stable transcript-element UUIDs), so identity survives a reordered /
 * drifted clip where the positional integer mis-identifies. Sorted (string
 * sort) so an unsorted caller still matches; joined on `|` (distinct from the
 * integer hash's `-`) so the two key spaces never collide in the shared
 * pending-edit map. Clean-break: an id-less (legacy) phrase produces `''` and
 * therefore never matches — it is dropped, never mis-applied by integer.
 */
function hashOriginalElementIds(originalElementIds: string[]): string {
  return [...originalElementIds].sort().join('|');
}

/**
 * Backfill `originalTranslatedValue` on every non-user-edited phrase that
 * doesn't already have one captured. Lets Revert work for any phrase the
 * user subsequently edits in this session, without waiting for the backend
 * to ship the captured-on-Generate version of this field.
 *
 * Rules:
 *   - `isUserEdited: true` with no captured original → leave alone. The
 *     stored value is NOT the machine original (somebody previously
 *     edited it and that edit was saved without capture). Backfilling
 *     here would lie.
 *   - `originalTranslatedValue` already set → leave alone. The backend
 *     (future PLAT-XXXX) or a prior session wins.
 *   - Otherwise → copy `translatedValue` into `originalTranslatedValue`.
 *     Safe because `!isUserEdited` guarantees the stored value is machine
 *     output.
 *
 * Called from loadTranslation / generateTranslation / regenerateTranslation —
 * every path that installs fresh translation data into the cache.
 * Exported so tests can assert expected shape without re-implementing the
 * rule inline.
 */
export function captureMachineOriginals(
  data: TranslationData,
): TranslationData {
  let changed = false;
  const nextPhrases = data.phrases.map(p => {
    if (p.originalTranslatedValue !== undefined) return p;
    if (p.isUserEdited) return p;
    changed = true;
    return { ...p, originalTranslatedValue: p.translatedValue };
  });
  // PLAT-10046 — same rule for composition phrases. The composition wire
  // shape uses `pinnedOverrideByManualEdit` as its "user-edited" flag (the
  // contract-locked field name from PLAT-9964); semantically it's the same
  // signal as `isUserEdited` on transcript phrases. Pinned entries are
  // left alone on backfill: their `translatedValue` IS the user's pin, not
  // the AI baseline, so copying it would lie. Non-pinned entries get the
  // baseline copied so a subsequent first-pin already has a captured
  // AI/preset baseline ready to surface in the pin-detail card and Revert.
  let compositionsChanged = false;
  const existingCompositions = Array.isArray(data.compositionPhrases)
    ? data.compositionPhrases
    : null;
  const nextCompositions = existingCompositions?.map(entry => {
    if (!entry || typeof entry.translatedValue !== 'string') return entry;
    if (entry.originalTranslatedValue !== undefined) return entry;
    if (entry.pinnedOverrideByManualEdit) return entry;
    compositionsChanged = true;
    return { ...entry, originalTranslatedValue: entry.translatedValue };
  });
  if (!changed && !compositionsChanged) return data;
  return {
    ...data,
    phrases: nextPhrases,
    ...(compositionsChanged && nextCompositions
      ? { compositionPhrases: nextCompositions }
      : {}),
  };
}
